// Core packages
import { formatDate, formatNumber } from "@angular/common";
import {
  AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild
} from "@angular/core";
import { FormControl, FormGroup } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";

// Third party packages
import {
  MatDialog, MatPaginator, MatSlideToggleChange,
  MatSort
} from "@angular/material";
import { FormlyFieldConfig, FormlyFormOptions } from "@ngx-formly/core";
import { TranslateService } from "@ngx-translate/core";
import * as FileSaver from "file-saver";
import { MatProgressButtonOptions } from "mat-progress-buttons";
import moment from "moment";
import { CookieService } from "ngx-cookie-service";
import { ToastrService } from "ngx-toastr";
import { combineLatest, forkJoin, fromEvent, merge, Subscription } from "rxjs";
import {
  debounceTime, distinctUntilChanged, finalize, startWith, take, tap
} from "rxjs/operators";


// Custom packages
import { AuthService } from "src/app/modules/auth/auth.service";
import { BalancesService } from "src/app/modules/balances/balances.service";
import { CompaniesService } from "src/app/modules/companies/companies.service";
import { DeadlinesService } from "src/app/modules/deadlines/deadlines.service";
import { PricesService } from "src/app/modules/prices/prices.service";
import { ExportContractsDialogComponent } from 'src/app/modules/properties/components/export-contracts-dialog/export-contracts-dialog.component';
import { ExportRasDocsDialogComponent } from 'src/app/modules/properties/components/export-ras-docs-dialog/export-ras-docs-dialog.component';
import { CondoStructuresService } from "src/app/modules/properties/condo-structures.service";
import { DocumentArchivesService } from "src/app/modules/properties/document-archives.service";
import DocumentTypeStructure from "src/app/modules/properties/documentTypes";
import { PropertiesService } from "src/app/modules/properties/properties.service";
import { StatementsService } from "src/app/modules/properties/statements.service";
import { SubscriptionsService } from "src/app/modules/subscriptions/subscriptions.service";
import { SuppliersService } from "src/app/modules/suppliers/suppliers.service";
import { UsersService } from "src/app/modules/users/users.service";
import { AddCommunicationDialogComponent } from "src/app/shared/components/dialogs/add-communication-dialog/add-communication-dialog.component";
import { ConfirmDialogComponent } from "src/app/shared/components/dialogs/confirm-dialog/confirm-dialog.component";
import { ListSetting } from "src/app/shared/interfaces/list-setting.interface";
import { ICompany } from "src/app/shared/models/company/company.interface";
import { Company } from "src/app/shared/models/company/company.model";
import { IPrice } from "src/app/shared/models/price/price.interface";
import { PropertyInterface } from "src/app/shared/models/property/property.interface";
import { Property } from "src/app/shared/models/property/property.model";
import { IArchediSubscription } from "src/app/shared/models/subscription/subscription.interface";
import { IUser } from "src/app/shared/models/user/user.interface";
import { User } from "src/app/shared/models/user/user.model";
import { DateTimeFormatPipe } from "src/app/shared/pipes/date-format.pipe";
import { ApiService } from "src/app/shared/services/api.service";
import { ConfigService } from "src/app/shared/services/config.service";
import { HelperService } from "src/app/shared/services/helper.service";
import { ListsService } from "src/app/shared/services/lists.service";
import { ThemeService } from "src/app/shared/services/theme.service";
import { BalancesDataSource } from "src/app/shared/utils/data-sources/balances-data-source";

@Component({
  selector: "app-property-balances",
  templateUrl: "./property-balances.page.html",
  styleUrls: ["./property-balances.page.scss"],
})
export class PropertyBalancesComponent
  implements OnInit, AfterViewInit, OnDestroy {
  private subscriptions: Subscription[] = [];
  loggedUser: IUser;
  loggedUserPermissions: string[];
  loggedUserRole: any;
  currentRole: any;
  lang = this.translateService.currentLang || this.translateService.defaultLang;
  // Could not be "Property" type because in "get" method in the back-end
  // We are populating some fields, so the model is not the same!
  public property: Property = new Property();
  propertyData: PropertyInterface;
  propertyId = "";
  loggedUserShared = false;
  loggedUserMaster = false;
  loggedUserAdmin = false;
  detailCardIsLoading = true;
  deleteCardIsLoading = true;
  sharedUserCardIsLoading = true;
  phonebookCardIsLoading = true;
  loggedUserRoleType = "";

  subscriptionDocumentUpload = false;

  dateTimeFormat: DateTimeFormatPipe;

  masterStarDateSupplier: Date;
  masterEndDateSupplier: Date;
  masterSuppliersFee: number;

  // BEGIN - Edit property form
  // BEGIN - ngx-formly
  form: FormGroup = new FormGroup({});
  formSC: FormGroup = new FormGroup({});
  formSP: FormGroup = new FormGroup({});
  model: any = {};
  modelSC: any = {};
  modelSP: any = {};
  anagraphicFields: FormlyFieldConfig[] = [];
  contractFields: FormlyFieldConfig[] = [];
  fieldsPrices: FormlyFieldConfig[];
  statusFields: FormlyFieldConfig[] = [];
  options: FormlyFormOptions = {};

  formSharedUser: FormGroup = new FormGroup({});
  modelSharedUser: any = {};
  sharedUserFields: FormlyFieldConfig[] = [];
  optionsSharedUser: FormlyFormOptions = {};
  // END - ngx-formly
  formBtnOptions: MatProgressButtonOptions = {
    active: false,
    text: this.translateService.instant("common.cta.save"),
    spinnerSize: 19,
    raised: true,
    stroked: true,
    flat: false,
    fab: false,
    buttonColor: "primary",
    spinnerColor: "primary",
    fullWidth: false,
    disabled: false,
    mode: "indeterminate",
    buttonIcon: {
      fontSet: "fal",
      fontIcon: "fa-save",
      inline: true,
    },
  };
  formSharedUserBtnOptions: MatProgressButtonOptions = {
    active: false,
    text: this.translateService.instant("common.cta.save"),
    spinnerSize: 19,
    raised: true,
    stroked: true,
    flat: false,
    fab: false,
    buttonColor: "primary",
    spinnerColor: "primary",
    fullWidth: false,
    disabled: false,
    mode: "indeterminate",
    buttonIcon: {
      fontSet: "fal",
      fontIcon: "fa-save",
      inline: true,
    },
  };
  deleteBtnOptions: MatProgressButtonOptions = {
    active: false,
    text: this.translateService.instant("common.cta.deactivate"),
    spinnerSize: 19,
    raised: true,
    stroked: true,
    flat: false,
    fab: false,
    buttonColor: "warn",
    spinnerColor: "warn",
    fullWidth: false,
    disabled: false,
    mode: "indeterminate",
    buttonIcon: {
      fontSet: "fal",
      fontIcon: "fa-times",
      inline: true,
    },
  };
  reactivateBtnOptions: MatProgressButtonOptions = {
    active: false,
    text: this.translateService.instant("common.cta.activate"),
    spinnerSize: 19,
    raised: true,
    stroked: true,
    flat: false,
    fab: false,
    buttonColor: "primary",
    spinnerColor: "primary",
    fullWidth: false,
    disabled: false,
    mode: "indeterminate",
    buttonIcon: {
      fontSet: "fal",
      fontIcon: "fa-check",
      inline: true,
    },
  };
  // END - Edit property form

  // BEGIN - Table balances
  balancesDisplayedColumns: string[] = [
    "index", 
    "number",
    "status",
    "total",
    "discount",
    "createdAt",
    "actions",
  ];
  balancesDataSource: BalancesDataSource;
  @ViewChild("balancesSort", { static: false }) balancesSort: MatSort;
  @ViewChild("balancesPaginator", { static: false })
  balancesPaginator: MatPaginator;
  @ViewChild("balancesInput", { static: false }) balancesInput: ElementRef;
  numberFilter: FormControl = new FormControl("");
  statusFilter: FormControl = new FormControl("");
  totalFilter: FormControl = new FormControl("");
  discountFilter: FormControl = new FormControl("");
  createdAtFilter: FormControl = new FormControl("");
  balancesCardIsLoading = true;
  // END - Table balances

  prices: IPrice[] = [];
  availableSubscriptions: any[];
  availableSubscriptionsKeys: any[];

  defaultStartDate: string;
  defaultEndDate: string;

  isInit = true;
  loggedUserRequestedRoleType: string;

  enableEdit = false;
  sharedUser: User = new User();

  previousPermissions = {
    unlimited: false,
    mappatura: false,
    gestionaleAmministrativo: false,
    amminImmobile: false,
    gestionaleContabile: false,
    urbanisticaEdEdilizia: false,
    catasto: false,
    contratti: false,
    lineaVita: false,
    amiantoDoc: false,
    edile: false,
    impiantoElettrico: false,
    riscaldamentoCondizionamento: false,
    ascensoriEMontauto: false,
    trattamentoAcqua: false,
    impiantiElettronici: false,
    antincendio: false,
    impiantiSpeciali: false,
    impiantiRicreativiSportivi: false,
    unitaImmobiliari: false,
    preventivi: false,
  };

  deadlinesCardIsLoading = true;
  
  public documentTypeStructure: DocumentTypeStructure =
    new DocumentTypeStructure();

  subscriptionType = "FREE";

  previousSafetyContract: any = {};
  previousAntifireContract: any = {};
  
  previousSubscription: any = {};

  availableTypes = [];

  archediSubscriptions: IArchediSubscription[] = [];

  // List of codes
  firstScanCodes = this.configService.availableScanCodes;
  firstFixedPackCodes = this.configService.availableFixedPackageCodes;
  scanInsNextCodesForfait = this.configService.availableInsCodesNextForfait;
  scanNextCodesForfait = this.configService.availableScanCodesNextForfait;

  previousTotalStorageSpaceUsed = 0;

  // Dati aggiuntivi elenco/nuovo elemento
  suppliersName: any[] = [];
  yearsOfStorage: any[] = [];
  conductorName: any[] = [];
  nameInterventionArchiving: any[] = [];

  documentTemplateId = "";
  documentTemplate: any[] = [];

  companyName = "";
  companyData = {};
  company: Company = new Company();

  mappaturaOpen = false;

  // Status card
  sagDate: FormControl = new FormControl("");
  statusCardIsLoading = true;

  /**
   * Class constructor
   */
  constructor(
    public dialog: MatDialog,
    public newMessageDialog: MatDialog,
    public cancelContractDialog: MatDialog,
    private configService: ConfigService,
    private activatedRoute: ActivatedRoute,
    private propertiesService: PropertiesService,
    private router: Router,
    private toastrService: ToastrService,
    private helperService: HelperService,
    private translateService: TranslateService,
    private themeService: ThemeService,
    private authService: AuthService,
    private cookieService: CookieService,
    private companiesService: CompaniesService,
    private apiService: ApiService, // DO NOT MOVE since it's used for 'autocomplete' field type
    private subscriptionsService: SubscriptionsService,
    private pricesService: PricesService,
    private balancesService: BalancesService,
    private listsService: ListsService,
    private userService: UsersService,
    private deadlinesService: DeadlinesService,
    private suppliersService: SuppliersService,
    private documentArchivesService: DocumentArchivesService,
    private condoStructuresService: CondoStructuresService,
    private statementsService: StatementsService,
  ) { }

  /**
   * Init components
   *
   * @since 1.0.0
   */
  ngOnInit(): void {
    this.propertyId = this.activatedRoute.snapshot.params.propertyId;
    
    // Set page title
    this.themeService.setPageTitle(
      this.translateService.instant("properties.detail.pageTitle")
    );

    // Get logged user data
    this.subscriptions.push(
      this.authService.loggedUser$.subscribe((res) => {
        this.loggedUser = res;
        const currentRole = this.loggedUser.userRoles.find((obj: any) => {
          return (
            obj._id.toString() ===
            this.authService.loggedUserRequestedRole$.getValue().toString()
          );
        });
        this.loggedUserRoleType = currentRole.role.type;
      })
    );
    this.subscriptions.push(
      this.authService.loggedUserPermissions$.subscribe(
        (res) => (this.loggedUserPermissions = res)
      )
    );

    // Get current lang
    this.lang =
      this.cookieService.get("lang") || this.configService.defaultLang;

    // Init data sources
    this.balancesDataSource = new BalancesDataSource(
      this.balancesService,
      this.helperService
    );

    // Init areas
    this.availableTypes = this.configService.availableDocumentAreas.map(
      (element: any) => {
        return {
          value: element,
          label: this.translateService.instant(
            `properties.documents.areas.${element}`
          ),
        };
      }
    );

    // Se default start and end date
    this.defaultStartDate = moment().format("YYYY-MM-DD"); // .toISOString();
    this.defaultEndDate = moment()
      .add(1, "years")
      .subtract(1, "days")
      .format("YYYY-MM-DD"); // .toISOString();

    this.getPrices();
  }

  /**
   * After view init logic
   *
   * @since 1.0.0
   */
  ngAfterViewInit(): void {
    // Get logged user's role data
    this.currentRole = this.loggedUser.userRoles.find((obj: any) => {
      return (
        obj._id.toString() ===
        this.authService.loggedUserRequestedRole$.getValue().toString()
      );
    });
    if (this.currentRole.role.type === "shared") {
      this.loggedUserShared = true;
    }
    if (
      this.currentRole.role.type === "master" ||
      this.currentRole.role.type === "collaborator"
    ) {
      this.loggedUserMaster = true;
      if (this.currentRole.company._id !== "") {
        this.subscriptions.push(
          this.companiesService
            .get(this.currentRole.company._id)
            .subscribe((res) => {
              if (res.status) {
                this.companyData = res.data;
                this.company.setData(res.data);
              }
            })
        );
      }
    }
      
    if (
      this.currentRole.role.type === "admin" ||
      this.currentRole.role.type === "operator"
    ) {
      this.loggedUserAdmin = true;
    }

    // Get requested property data
    this.subscriptions.push(
      this.propertiesService
        .get(this.activatedRoute.snapshot.params.propertyId)
        .subscribe(
          (res) => {
            if (res.status) {
              // Set permission data
              this.property.setData(res.data);
              this.propertyData = res.data;

              for (const prop in res.data.subscription) {
                this.previousSubscription[prop] =
                  res.data.subscription[prop];
              }

              if (
                this.propertyData.subscription &&
                this.propertyData.subscription.key
              ) {
                this.subscriptionType = this.propertyData.subscription.key;
                if (this.propertyData.subscription.documentUpload) {
                  this.subscriptionDocumentUpload = true;
                }
              }

              if (this.propertyData.structure) {
                this.subscriptions.push(
                  this.condoStructuresService.get(this.propertyData.structure)
                    .subscribe(res => {
                      if (
                        res.status && 
                        res.data
                      ) {
                        this.mappaturaOpen = res.data.open;
                      }
                    },
                      err => {
                        this.helperService.handleError(err);
                      })
                );
              }

              // Stop loading in delete form
              this.deleteCardIsLoading = false;

              // Get form initial values and init the form
              this.getDataAndInitForm();

              return;
            }

            // Something bad happened
            // redirect user to home
            this.router.navigate([this.lang, "app", "properties", "list"]);
          },
          (err) => {
            // if we came here users with given userId do not exist
            this.router.navigate([this.lang, "errors", "404"]);
          }
        )
    );
  }

  /**
   * Handle component destroy
   *
   * @since 1.0.0
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this.subscriptions.forEach((sub) => sub.unsubscribe());

    if (this.balancesPaginator) {
      // Push current list settings to the listsService
      this.listsService.setListSetting(
        "balances",
        this.balancesPaginator.pageIndex.valueOf(),
        this.balancesPaginator.pageSize.valueOf(),
        {
          general: this.balancesInput.nativeElement.value,
          number: this.numberFilter.value,
          status: this.statusFilter.value,
          total: this.totalFilter.value,
          discount: this.discountFilter.value,
          createdAt: this.createdAtFilter.value,
        }
      );
    }

  }

  /**
   * Get prices from backend
   *
   * @since 1.0.0
   */
  getPrices(): void {
    this.subscriptions.push(
      this.pricesService.getList(0, 15).subscribe((res) => {
        if (res.status) {
          this.prices.push(...res.data.items);
          this.getSubscriptions();
        }
      })
    );
  }

  /**
   * Load requested subscription data
   *
   * @since 1.0.0
   */
  getSubscriptions(): void {
    this.subscriptionsService.getList().subscribe(
      (res) => {
        if (res.status) {
          this.archediSubscriptions = res.data.items;
          const subscriptions = res.data.items.map((item: any) => {
            return {
              value: item,
              label: item.name,
            };
          });
          this.availableSubscriptions = subscriptions.filter((element: any) => {
            return element.value.key !== "QRANTINCENDIO";
          });
          this.availableSubscriptionsKeys = this.availableSubscriptions.map(
            (element: any) => {
              return {
                value: element.value.key,
                label: element.value.name,
              };
            }
          );

          // Create an empty value
          const noSubscription = {
            value: "noSubscription",
            label: this.translateService.instant(
              "subscriptions.fields.noSubscription"
            ),
          };
          this.availableSubscriptionsKeys.push(noSubscription);
        }
      },
      (err) => {
        this.helperService.handleError(err);
      }
    );
  }

  /**
   * Get requested initialValues for the form and than init it
   *
   * @since 1.0.0
   */
  getDataAndInitForm(): void {
    if (this.property.get("owner._id")) {
      this.subscriptions.push(
        forkJoin(
          this.companiesService.get(this.property.get("admin.company._id")),
          this.companiesService.get(this.property.get("owner._id"))
        ).subscribe((res) => {
          let initialAdminData;
          let initialOwnerData;
          if (res[0] && res[0].status) {
            initialAdminData = [res[0].data];
            this.companyName = res[0].data.name;
          }
          if (res[1] && res[1].status) {
            initialOwnerData = [res[1].data];
          }

          // Init edit form
          this.initForm(initialAdminData, initialOwnerData);

          // Make detail form card visible by disabling his card-spinner
          this.detailCardIsLoading = false;

          if (!this.loggedUserMaster) {
            // Subscribe to listsService and update this list settings
            if (this.loggedUserPermissions.includes("canReadBalancesList")) {
              this.subscribeToListsSettings("balances");
              this.initBalancesDataTable();
            }
            this.balancesCardIsLoading = false;
          }

          this.statusCardIsLoading = false;
        })
      );
    } else {
      this.companiesService
        .get(this.property.get("admin.company._id"))
        .subscribe((res) => {
          let initialAdminData;
          if (res && res.status) {
            initialAdminData = [res.data];
            this.companyName = res.data.name;
          }

          // Init edit form
          this.initForm(initialAdminData);

          // Make detail form card visible by disabling his card-spinner
          this.detailCardIsLoading = false;

          if (!this.loggedUserMaster) {
            // Subscribe to listsService and update this list settings
            if (this.loggedUserPermissions.includes("canReadBalancesList")) {
              this.subscribeToListsSettings("balances");
              this.initBalancesDataTable();
            }
            this.balancesCardIsLoading = false;

          }

          this.statusCardIsLoading = false;
        });
    }

    if (this.property.get("sharedUser")) {
      this.subscriptions.push(
        this.userService.get(this.property.get("sharedUser")).subscribe(
          (res) => {
            if (res.status) {
              this.sharedUser.setData(res.data);
              this.initShareUserFom();
              this.sharedUserCardIsLoading = false;
              return;
            }
          },
          (err) => {
            this.router.navigate([this.lang, "errors", "404"]);
          }
        )
      );
    }
  }

  /**
   * Init form
   *
   * @since 1.0.0
   */
  initForm(initialAdminData?: any, initialOwnerData?: any): void {
    if (initialAdminData && Array.isArray(initialAdminData) && initialAdminData.length) {
      if (initialAdminData[0].subscription) {
        Object.keys(initialAdminData[0].subscription).forEach((key) => {
          if (key === 'startDateSuppliers') {
            this.masterStarDateSupplier = initialAdminData[0].subscription[key];
          }
          if (key === 'endDateSuppliers') {
            this.masterEndDateSupplier = initialAdminData[0].subscription[key];
          }
          if (key === 'suppliersFee') {
            this.masterSuppliersFee = initialAdminData[0].subscription[key];
          }
        });
      }
    }
    // Set model
    this.model = this.property.getData();
    this.modelSC = this.property.getData();
    if (this.modelSC.subscription.totalStorageSpaceUsed) {
      this.previousTotalStorageSpaceUsed =
        this.modelSC.subscription.totalStorageSpaceUsed;
      this.modelSC.subscription.totalStorageSpaceUsed = formatNumber(
        this.modelSC.subscription.totalStorageSpaceUsed,
        "it-IT"
      );
    }

    if (
      this.propertyData.propertyStatus &&
      this.propertyData.propertyStatus.mappingCompleted &&
      this.propertyData.propertyStatus.mappingCompleted.user
    ) {
      this.propertyData.propertyStatus.mappingCompleted.username = 
        `${this.propertyData.propertyStatus.mappingCompleted.user.firstName} ${this.propertyData.propertyStatus.mappingCompleted.user.lastName}`
    }

    this.propertyData.propertyStatus.startDate.date = this.propertyData.subscription.startDate;

    this.modelSP.propertyStatus = this.propertyData.propertyStatus;

    // For price list sorting
    let index = 0;

    // Minimum char amount to activate search filtering
    const minTermLength = 1;

    // Set price
    if (this.modelSC.subscription.prices) {
      this.modelSC.subscription.prices.forEach((elem, index) => {
        switch (index) {
          case 0:
            this.modelSC.subscription.price1 = { discount: elem.discount };
            break;
          case 1:
            this.modelSC.subscription.price2 = { discount: elem.discount };
            break;
          case 2:
            this.modelSC.subscription.price3 = { discount: elem.discount };
            break;
          case 3:
            this.modelSC.subscription.price4 = { discount: elem.discount };
            break;
          case 4:
            this.modelSC.subscription.price5 = { discount: elem.discount };
            break;
          case 5:
            this.modelSC.subscription.price6 = { discount: elem.discount };
            break;
          case 6:
            this.modelSC.subscription.price7 = { discount: elem.discount };
            break;
          case 7:
            this.modelSC.subscription.price8 = { discount: elem.discount };
            break;
          case 8:
            this.modelSC.subscription.price9 = { discount: elem.discount };
            break;
          case 9:
            this.modelSC.subscription.price10 = { discount: elem.discount };
            break;
          case 10:
            this.modelSC.subscription.price11 = { discount: elem.discount };
            break;
        }
      });
    }

    this.model.admin.company =
      this.model &&
        this.model.admin &&
        this.model.admin.company &&
        this.model.admin.company._id
        ? this.model.admin.company._id
        : undefined;
    this.model.owner =
      this.model && this.model.owner && this.model.owner._id
        ? this.model.owner._id
        : undefined;

    // Set fields
    this.anagraphicFields = [
      // ...this.anagraphicFields,
      {
        fieldGroupClassName: "row",
        fieldGroup: [
          {
            className: "col-12 col-md-6",
            type: "autocomplete",
            key: "admin.company",
            templateOptions: {
              customLabel: this.translateService.instant(
                "properties.fields.admin"
              ),
              placeholder: this.translateService.instant(
                "properties.fields.admin"
              ),
              required: true,
              bindLabel: "name",
              bindValue: "_id",
              multiple: false,
              search$: this.companiesService.getList.bind(this),
              initialValues: initialAdminData,
              hideSelected: true,
              disabled: this.loggedUserMaster,
              minTermLength,
              typeToSearchText: this.translateService.instant(
                "common.words.typeToSearch",
                { minTermLength }
              ),
              clearAllText: this.translateService.instant(
                "common.words.clearAll"
              ),
              notFoundText: this.translateService.instant(
                "common.words.notFound"
              ),
              loadingText: this.translateService.instant(
                "common.words.loading"
              ),
              callback: (data: ICompany): void => {
                this.onAdminCompanySelected(data);
              },
            },
          },
          {
            className: "col-12 col-md-6",
            type: "autocomplete",
            key: "owner",
            templateOptions: {
              customLabel: this.translateService.instant(
                "properties.fields.owner"
              ),
              placeholder: this.translateService.instant(
                "properties.fields.owner"
              ),
              required: false,
              bindLabel: "name",
              bindValue: "_id",
              multiple: false,
              search$: this.companiesService.getList.bind(this),
              initialValues: initialOwnerData,
              hideSelected: true,
              // addTag: true,
              disabled: this.loggedUserMaster,
              minTermLength,
              typeToSearchText: this.translateService.instant(
                "common.words.typeToSearch",
                { minTermLength }
              ),
              clearAllText: this.translateService.instant(
                "common.words.clearAll"
              ),
              notFoundText: this.translateService.instant(
                "common.words.notFound"
              ),
              // addTagText: this.translateService.instant("common.words.addTag"),
              loadingText: this.translateService.instant(
                "common.words.loading"
              ),
            },
          },
          {
            className: "col-12 col-md-4 col-xl-3",
            type: "select",
            key: "type",
            templateOptions: {
              label: this.translateService.instant("properties.fields.type"),
              placeholder: this.translateService.instant(
                "properties.fields.type"
              ),
              required: true,
              disabled: this.loggedUserMaster,
              options: [
                {
                  value: "co",
                  label: this.translateService.instant("properties.types.co"),
                },
                {
                  value: "bsc",
                  label: this.translateService.instant("properties.types.bsc"),
                },
                {
                  value: "pur",
                  label: this.translateService.instant("properties.types.pur"),
                },
                {
                  value: "puc",
                  label: this.translateService.instant("properties.types.puc"),
                },
                {
                  value: "sp",
                  label: this.translateService.instant("properties.types.sp"),
                },
              ],
            },
          },
          {
            className: "col-12 col-md-4 col-xl-3",
            type: "input",
            key: "name",
            templateOptions: {
              label: this.translateService.instant("properties.fields.name"),
              placeholder: this.translateService.instant(
                "properties.fields.name"
              ),
              required: true,
              disabled: this.loggedUserMaster,
              minLength: 2,
              maxLength: 100,
            },
          },
          {
            className: "col-12 col-md-4 col-xl-3",
            type: "input",
            key: "notifyMail1",
            templateOptions: {
              label: this.translateService.instant(
                "properties.fields.notifyMail1"
              ),
              placeholder: this.translateService.instant(
                "properties.fields.notifyMail1"
              ),
              required: true,
              minLength: 5,
              maxLength: 320,
              type: "email",
            },
          },
          {
            className: "col-12 col-md-4 col-xl-3",
            type: "input",
            key: "notifyMail2",
            templateOptions: {
              label: this.translateService.instant(
                "properties.fields.notifyMail2"
              ),
              placeholder: this.translateService.instant(
                "properties.fields.notifyMail2"
              ),
              required: false,
              minLength: 5,
              maxLength: 320,
              type: "email",
            },
          },
          {
            className: "col-12 col-md-12",
            type: "address",
            key: "address",
            templateOptions: {
              label: this.translateService.instant("properties.fields.address"),
              placeholder: this.translateService.instant(
                "properties.fields.address"
              ),
              required: false,
              disabled: this.loggedUserMaster,
            },
          },
          {
            className: "col-12 col-sm-3",
            type: "input",
            key: "taxCode",
            templateOptions: {
              label: this.translateService.instant("properties.fields.taxCode"),
              placeholder: this.translateService.instant(
                "properties.fields.taxCode"
              ),
              required: true,
              disabled: this.loggedUserMaster,
              minLength: 0,
              maxLength: 20,
            },
          },
          {
            className: "col-12 col-sm-3",
            type: "input",
            key: "sdiCode",
            templateOptions: {
              label: this.translateService.instant("properties.fields.sdiCode"),
              placeholder: this.translateService.instant(
                "properties.fields.sdiCode"
              ),
              required: false,
              disabled: this.loggedUserMaster,
              minLength: 6,
              maxLength: 7,
            },
          },
          {
            className: "col-12 col-sm-3",
            type: "day-month-picker",
            key: "administrationStartDate",
            templateOptions: {
              label: this.translateService.instant(
                "properties.fields.administrationStartDate"
              ),
              placeholder: this.translateService.instant(
                "properties.fields.administrationStartDate"
              ),
              minLength: 2,
              maxLength: 100,
            },
            modelOptions: {
              updateOn: "change",
            },
            // expressionProperties: {
            //   "templateOptions.datepickerOptions.max":
            //     "model.administrationEndDate",
            // },
          },
          {
            className: "col-12 col-sm-3",
            type: "day-month-picker",
            key: "administrationEndDate",
            templateOptions: {
              label: this.translateService.instant(
                "properties.fields.administrationEndDate"
              ),
              placeholder: this.translateService.instant(
                "properties.fields.administrationEndDate"
              ),
              minLength: 2,
              maxLength: 100,
            },
            modelOptions: {
              updateOn: "change",
            },
            // expressionProperties: {
            //   "templateOptions.datepickerOptions.min":
            //     "model.administrationStartDate",
            // },
          },
          {
            className: "col-12 col-sm-6",
            type: "input",
            key: "iban",
            templateOptions: {
              label: this.translateService.instant("properties.fields.iban"),
              placeholder: this.translateService.instant(
                "properties.fields.iban"
              ),
              required: false,
              minLength: 27,
              maxLength: 27,
            },
          },
          {
            className: "col-12 col-sm-3",
            type: "checkbox",
            key: "dossier",
            templateOptions: {
              label: this.translateService.instant("properties.fields.dossier"),
              placeholder: this.translateService.instant(
                "properties.fields.dossier"
              ),
              required: false,
              disabled: this.loggedUserMaster,
            },
          },
          {
            className: "col-12 col-sm-3",
            type: "input",
            key: "dossierEmail",
            templateOptions: {
              label: this.translateService.instant("properties.fields.dossierEmail"),
              placeholder: this.translateService.instant(
                "properties.fields.dossierEmail"
              ),
              required: false,
            },
            expressionProperties: {
              "templateOptions.disabled": (): boolean => {
                return this.loggedUserMaster || !this.model.dossier;
              },
              "templateOptions.required": (): boolean => {
                return this.model.dossier;
              },
            },
          },
        ],
      },
    ];

    this.contractFields = [
      // 1. PLATFORM SUBSCRIPTION
      {
        fieldGroupClassName: "row",
        hooks: {
          onInit: (): void => {
            this.updateSubscriptionCodes();
          },
        },
        fieldGroup: [
          {
            className: "col-12 boxForm",
            template:
              "<div><strong>" +
              this.translateService.instant(
                "companies.fields.subscription.subscription"
              ) +
              "</strong></div><br />",
          },
          {
            className: "col-12 col-md-4",
            type: "checkbox",
            key: "subscription.documentUpload",
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.documentUpload.title"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.documentUpload.title"
              ),
            },
            expressionProperties: {
              "templateOptions.disabled": (): boolean => {
                return !this.enableEdit;
              },
            },
          },
          {
            className: "col-0 col-md-6",
          },  
          {
            className: "col-12 col-md-4",
            type: "select",
            key: "subscription.invoiceHolder",
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.invoiceHolder.title"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.invoiceHolder.title"
              ),
              required: true,
              multiple: false,
              options: [
                {
                  value: "property",
                  label: this.translateService.instant(
                    "companies.fields.subscription.invoiceHolder.property"
                  ),
                },
                {
                  value: "admin",
                  label: this.translateService.instant(
                    "companies.fields.subscription.invoiceHolder.admin"
                  ),
                },
                {
                  value: "owner",
                  label: this.translateService.instant(
                    "companies.fields.subscription.invoiceHolder.owner"
                  ),
                },
              ],
            },
            expressionProperties: {
              "templateOptions.disabled": (): boolean => {
                return !this.enableEdit || !this.modelSC.subscription.documentUpload;
              },
            },
          },
          {
            className: "col-0 col-md-6",
          },
          {
            className: "col-4",
            type: "select",
            key: "subscription.key",
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.subscriptionType"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.subscriptionType"
              ),
              multiple: false,
              options: this.availableSubscriptionsKeys,
              required: true,
            },
            expressionProperties: {
              "templateOptions.disabled": (): boolean => {
                return !this.enableEdit || !this.modelSC.subscription.documentUpload;
              },
            },
          },
          {
            className: "col-4",
            type: "input",
            key: "subscription.fee",
            templateOptions: {
              label: this.translateService.instant("subscriptions.fields.fee"),
              placeholder: this.translateService.instant(
                "subscriptions.fields.fee"
              ),
              type: "number",
              step: 0.1,
              min: 0,
              max: 10000,
            },
            expressionProperties: {
              "templateOptions.required":
                "model.subscription.key && model.subscription.key !== 'noSubscription'",
              "templateOptions.disabled": (): boolean => {
                return (
                  !this.enableEdit ||
                  !this.modelSC.subscription.documentUpload ||
                  (this.modelSC.subscription.key &&
                    this.modelSC.subscription.key === "noSubscription")
                );
              },
            },
          },
          {
            className: "col-0 col-md-4",
          },
          {
            className: "col-12 col-md-4",
            type: "input",
            key: "subscription.availableSpace",
            templateOptions: {
              label: this.translateService.instant(
                "subscriptions.fields.availableSpace"
              ),
              placeholder: this.translateService.instant(
                "subscriptions.fields.availableSpace"
              ),
              type: "number",
              min: 0,
              max: 1000,
            },
            expressionProperties: {
              "templateOptions.required":
                "model.subscription.key && model.subscription.key !== 'noSubscription'",
              "templateOptions.disabled": (): boolean => {
                return (
                  !this.enableEdit ||
                  !this.modelSC.subscription.documentUpload ||
                  (this.modelSC.subscription.key &&
                    this.modelSC.subscription.key === "noSubscription")
                );
              },
            },
          },
          {
            className: "col-12 col-md-4",
            type: "input",
            key: "subscription.totalStorageSpaceUsed",
            templateOptions: {
              label: this.translateService.instant(
                "subscriptions.fields.totalStorageSpaceUsed"
              ),
              placeholder: this.translateService.instant(
                "subscriptions.fields.totalStorageSpaceUsed"
              ),
              // type: "number",
              // step: 0.001,
              // min: 0,
              // max: 50000,
              disabled: true,
            },
          },
          {
            className: "col-0 col-md-4",
          },
        ],
      },
      // 2. STRUCTURE SUBSCRIPTION
      {
        fieldGroupClassName: "row",
        fieldGroup: [
          {
            className: "col-12",
            template:
              "<hr /><div><strong>" +
              this.translateService.instant(
                "companies.fields.subscription.structure.sectionTitle"
              ) +
              "</strong></div><br />",
          },
          {
            className: "col-12 col-md-2",
            type: "checkbox",
            key: "subscription.structure",
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.structure.title"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.structure.title"
              ),
            },
            expressionProperties: {
              "templateOptions.required":
                "model.subscription.key && model.subscription.key !== 'noSubscription' && model.subscription.key !== 'FREE' && model.subscription.key !== 'FREEBASIC'",
              "templateOptions.disabled": (): boolean => {
                return (
                  !this.enableEdit ||
                  !this.modelSC.subscription.documentUpload ||
                  (this.modelSC.subscription.key &&
                    (this.modelSC.subscription.key === "noSubscription" ||
                      this.modelSC.subscription.key === "FREE" ||
                      this.modelSC.subscription.key === "FREEBASIC"))
                );
              },
            },
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: "subscription.structurePrice",
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.structurePrice"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.structurePrice"
              ),
              type: "number",
              step: 0.1,
              min: 0,
              max: 10000,
            },
            expressionProperties: {
              "templateOptions.required":
                "model.subscription.key && model.subscription.structure",
              "templateOptions.disabled": (): boolean => {
                return (
                  !this.enableEdit ||
                  !this.modelSC.subscription.documentUpload ||
                  (this.modelSC.subscription.key &&
                    (this.modelSC.subscription.key === "noSubscription" ||
                      this.modelSC.subscription.key === "FREE" ||
                      this.modelSC.subscription.key === "FREEBASIC" ||
                      !this.modelSC.subscription.structure))
                );
              },
            },
          },
        ],
      },
      // 3. FIRST BALANCE
      {
        fieldGroupClassName: "row",
        fieldGroup: [
          {
            className: "col-12",
            template:
              "<hr /><div><strong>" +
              this.translateService.instant(
                "companies.fields.subscription.firstBalance.sectionTitle"
              ) +
              "</strong></div><br />",
          },
          {
            className: "col-12 col-md-2",
            type: "checkbox",
            key: "subscription.firstPriceList",
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.firstPriceList.title"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.firstPriceList.title"
              ),
            },
            expressionProperties: {
              "templateOptions.required":
                "model.subscription.key && model.subscription.key !== 'noSubscription'",
              "templateOptions.disabled": (): boolean => {
                return (
                  !this.enableEdit ||
                  !this.modelSC.subscription.documentUpload ||
                  (this.modelSC.subscription.key &&
                    this.modelSC.subscription.key === "noSubscription")
                );
              },
            },
          },
          {
            className: "col-1",
          },
        ],
      },
      // 4. SUBSEQUENT INSERTIONS
      {
        fieldGroupClassName: "row",
        fieldGroup: [
          {
            className: "col-12",
            template:
              "<hr /><div><strong>" +
              this.translateService.instant(
                "companies.fields.subscription.subsequentInsertions.sectionTitle"
              ) +
              "</strong></div><br />",
          },
          {
            className: "col-12 col-md-2",
            type: "checkbox",
            key: "subscription.scanInsNext",
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.scanInsNext.title"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.scanInsNext.title"
              ),
            },
            expressionProperties: {
              "templateOptions.required":
                "model.subscription.key && model.subscription.key !== 'noSubscription'",
              "templateOptions.disabled": (): boolean => {
                return (
                  !this.enableEdit ||
                  !this.modelSC.subscription.documentUpload ||
                  (this.modelSC.subscription.key &&
                    (this.modelSC.subscription.key === "noSubscription" ||
                      
                      this.modelSC.subscription.flatRate))
                );
              },
            },
          },
          {
            className: "col-10",
          },
          {
            className: "col-12 col-md-2",
            type: "checkbox",
            key: "subscription.flatRate",
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.flatRate.title"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.flatRate.title"
              ),
            },
            expressionProperties: {
              "templateOptions.required":
                "model.subscription.key && model.subscription.key !== 'noSubscription'",
              "templateOptions.disabled": (): boolean => {
                return (
                  !this.enableEdit ||
                  !this.modelSC.subscription.documentUpload ||
                  (this.modelSC.subscription.key &&
                    (this.modelSC.subscription.key === "noSubscription" ||
                      
                      this.modelSC.subscription.scanInsNext))
                );
              },
            },
          },
          {
            className: "col-2",
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: "subscription.totalDoc",
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.flatRate.totalDoc"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.flatRate.totalDoc"
              ),
              required: false,
              type: "number",
              step: 1,
              min: 0,
              disabled: true,
            },
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: "subscription.totalPdf",
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.flatRate.totalPdf"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.flatRate.totalPdf"
              ),
              required: false,
              type: "number",
              step: 1,
              min: 0,
              disabled: true,
            },
          },
        ],
      },
      // 5. SUBSEQUENT SCANS
      {
        fieldGroupClassName: "row",
        fieldGroup: [
          {
            className: "col-12",
            template:
              "<hr /><div><strong>" +
              this.translateService.instant(
                "companies.fields.subscription.subsequentScans.sectionTitle"
              ) +
              "</strong></div><br />",
          },
          {
            className: "col-12 col-md-2",
            type: "checkbox",
            key: "subscription.scanNext",
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.scanNext.title"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.scanNext.title"
              ),
            },
            expressionProperties: {
              "templateOptions.required":
                "model.subscription.key && model.subscription.key !== 'noSubscription'",
              "templateOptions.disabled": (): boolean => {
                return (
                  !this.enableEdit ||
                  !this.modelSC.subscription.documentUpload ||
                  (this.modelSC.subscription.key &&
                    (this.modelSC.subscription.key === "noSubscription" ||
                      
                      this.modelSC.subscription.flatRateScan))
                );
              },
            },
          },
          {
            className: "col-10",
          },
          {
            className: "col-12 col-md-2",
            type: "checkbox",
            key: "subscription.flatRateScan",
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.flatRateScan.title"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.flatRateScan.title"
              ),
            },
            expressionProperties: {
              "templateOptions.required":
                "model.subscription.key && model.subscription.key !== 'noSubscription'",
              "templateOptions.disabled": (): boolean => {
                return (
                  !this.enableEdit ||
                  !this.modelSC.subscription.documentUpload ||
                  (this.modelSC.subscription.key &&
                    (this.modelSC.subscription.key === "noSubscription" ||
                      
                      this.modelSC.subscription.scanNext))
                );
              },
            },
          },
          
          {
            className: "col-10",
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: "subscription.totalA4",
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.flatRate.totalA4"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.flatRate.totalA4"
              ),
              required: false,
              type: "number",
              step: 1,
              min: 0,
              disabled: true,
            },
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: "subscription.totalA3",
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.flatRate.totalA3"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.flatRate.totalA3"
              ),
              required: false,
              type: "number",
              step: 1,
              min: 0,
              disabled: true,
            },
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: "subscription.totalA2",
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.flatRate.totalA2"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.flatRate.totalA2"
              ),
              required: false,
              type: "number",
              step: 1,
              min: 0,
              disabled: true,
            },
          },
          {
            className: "col-1",
          },
          {
            className: "col-2",
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: "subscription.totalA1",
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.flatRate.totalA1"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.flatRate.totalA1"
              ),
              required: false,
              type: "number",
              step: 1,
              min: 0,
              disabled: true,
            },
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: "subscription.totalA0",
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.flatRate.totalA0"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.flatRate.totalA0"
              ),
              required: false,
              type: "number",
              step: 1,
              min: 0,
              disabled: true,
            },
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: "subscription.totalOutFormat",
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.flatRate.totalOutFormat"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.flatRate.totalOutFormat"
              ),
              required: false,
              type: "number",
              step: 1,
              min: 0,
              disabled: true,
            },
          },
          {
            className: "col-1",
          },
        ],
      },
      // 6. SUBSCRIPTION OPTIONS
      {
        fieldGroupClassName: "row",
        fieldGroup: [
          {
            className: "col-12",
            template:
              "<hr /><div><strong>" +
              this.translateService.instant(
                "companies.fields.subscription.optionsNfc.sectionTitle"
              ) +
              "</strong></div><br />",
          },
          {
            className: "col-12 col-md-4",
            type: "checkbox",
            key: "subscription.nfcAccess",
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.nfcAccess"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.nfcAccess"
              ),
              required: false,
            },
            expressionProperties: {
              "templateOptions.disabled": (): boolean => {
                return (
                  !this.enableEdit
                );
              },
            },
          },
          {
            className: "col-12 col-md-4",
            type: "input",
            key: "subscription.nfcAccessFee",
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.nfcAccessFee"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.nfcAccessFee"
              ),
              type: "number",
              step: 0.1,
              min: 0,
              max: 10000,
            },
            expressionProperties: {
              "templateOptions.required": "model.subscription.nfcAccess",
              "templateOptions.disabled": (): boolean => {
                return (
                  !this.enableEdit ||
                  !this.modelSC.subscription.nfcAccess
                );
              },
            },
          },
          {
            className: "col-md-4",
          },
          {
            className: "col-12 col-md-4",
            type: "datepicker",
            key: "subscription.startDateNfcAccess",
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.startDateNfcAccess"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.startDateNfcAccess"
              ),
            },
            expressionProperties: {
              "templateOptions.required":
                "model.subscription.nfcAccess",
              "templateOptions.disabled": (): boolean => {
                return (
                  !this.enableEdit ||
                  !this.modelSC.subscription.nfcAccess
                );
              },
            },
          },
          {
            className: "col-12 col-md-4",
            type: "datepicker",
            key: "subscription.endDateNfcAccess",
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.endDateNfcAccess"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.endDateNfcAccess"
              ),
            },
            expressionProperties: {
              "templateOptions.required":
                "model.subscription.nfcAccess",
              "templateOptions.disabled": (): boolean => {
                return (
                  !this.enableEdit ||
                  !this.modelSC.subscription.nfcAccess
                );
              },
            },
          },
          {
            className: "col-12",
            template:
              "<hr /><div><strong>" +
              this.translateService.instant(
                "companies.fields.subscription.optionsSupplierEvaluation.sectionTitle"
              ) +
              "</strong></div><br />",
          },
          {
            className: "col-12 col-md-4",
            type: "checkbox",
            key: "subscription.suppliers",
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.suppliers"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.suppliers"
              ),
              required: false,
            },
            expressionProperties: {
              "templateOptions.disabled": (): boolean => {
                return (
                  !this.enableEdit
                );
              },
            },
          },
          {
            className: "col-12 col-md-4",
            type: "input",
            key: "subscription.suppliersFee",
            templateOptions: {
              label: this.translateService.instant("subscriptions.fields.fee"),
              placeholder: this.translateService.instant(
                "subscriptions.fields.fee"
              ),
              type: "number",
              step: 0.1,
              min: 0,
              max: 10000,
            },
            expressionProperties: {
              "templateOptions.required":
                "model.subscription.suppliers",
              "templateOptions.disabled": (): boolean => {
                return (
                  !this.enableEdit
                );
              },
            },
          },
          {
            className: "col-md-4",
          },
          {
            className: "col-12 col-md-4",
            type: "datepicker",
            key: "subscription.startDateSuppliers",
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.startDateSuppliers"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.startDateSuppliers"
              ),
              disabled: true,
            },
            expressionProperties: {
              "templateOptions.required":
                "model.subscription.suppliers",
            },
          },
          {
            className: "col-12 col-md-4",
            type: "datepicker",
            key: "subscription.endDateSuppliers",
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.endDateSuppliers"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.endDateSuppliers"
              ),
              disabled: true,
            },
            expressionProperties: {
              "templateOptions.required":
                "model.subscription.suppliers",
            },
          },
          {
            className: "col-md-8",
          },
        ],
      },
      // 7. PRICE LIST
      {
        fieldGroupClassName: "row",
        fieldGroup: [
          {
            className: "col-12",
            template:
              "<hr /><div><strong>" +
              this.translateService.instant(
                "companies.fields.subscription.priceList.title"
              ) +
              "</strong></div><br />",
          },
          {
            className: "col-12 section-label sub-section-label",
            template:
              "<div>" +
              this.translateService.instant(
                `prices.types.${this.prices[index].name}`
              ) +
              "</div>",
          },
          {
            key: `subscription.prices[${index}].key`,
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: `subscription.prices[${index}].price`,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.priceList.price"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.priceList.priceService1"
              ),
              required: false,
              type: "number",
              step: 0.1,
              min: 0,
              max: 100,
            },
            expressionProperties: {
              "templateOptions.disabled": (): boolean => {
                return !this.enableEdit;
              },
            },
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: `subscription.prices[${index++}].discount`,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.priceList.discount"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.priceList.discount"
              ),
              required: false,
              type: "number",
              step: 1,
              min: 0,
              max: 100,
            },
            expressionProperties: {
              "templateOptions.disabled": (): boolean => {
                return !this.enableEdit;
              },
            },
          },
          {
            className: "col-0 col-md-6",
          },
          {
            className: "col-12 section-label sub-section-label",
            template:
              "<div>" +
              this.translateService.instant(
                `prices.types.${this.prices[index].name}`
              ) +
              "</div>",
          },
          {
            key: `subscription.prices[${index}].key`,
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: `subscription.prices[${index}].price`,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.priceList.price"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.priceList.priceService1"
              ),
              required: false,
              type: "number",
              step: 0.1,
              min: 0,
              max: 100,
            },
            expressionProperties: {
              "templateOptions.disabled": (): boolean => {
                return !this.enableEdit;
              },
            },
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: `subscription.prices[${index++}].discount`,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.priceList.discount"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.priceList.discount"
              ),
              required: false,
              type: "number",
              step: 1,
              min: 0,
              max: 100,
            },
            expressionProperties: {
              "templateOptions.disabled": (): boolean => {
                return !this.enableEdit;
              },
            },
          },
          {
            className: "col-0 col-md-6",
          },
          {
            className: "col-12 section-label sub-section-label",
            template:
              "<div>" +
              this.translateService.instant(
                `prices.types.${this.prices[index].name}`
              ) +
              "</div>",
          },
          {
            key: `subscription.prices[${index}].key`,
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: `subscription.prices[${index}].price`,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.priceList.price"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.priceList.priceService1"
              ),
              required: false,
              type: "number",
              step: 0.1,
              min: 0,
              max: 100,
            },
            expressionProperties: {
              "templateOptions.disabled": (): boolean => {
                return !this.enableEdit;
              },
            },
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: `subscription.prices[${index++}].discount`,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.priceList.discount"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.priceList.discount"
              ),
              required: false,
              type: "number",
              step: 1,
              min: 0,
              max: 100,
            },
            expressionProperties: {
              "templateOptions.disabled": (): boolean => {
                return !this.enableEdit;
              },
            },
          },
          {
            className: "col-0 col-md-6",
          },
          {
            className: "col-12 section-label sub-section-label",
            template:
              "<div>" +
              this.translateService.instant(
                `prices.types.${this.prices[index].name}`
              ) +
              "</div>",
          },
          {
            key: `subscription.prices[${index}].key`,
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: `subscription.prices[${index}].price`,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.priceList.price"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.priceList.priceService1"
              ),
              required: false,
              type: "number",
              step: 0.1,
              min: 0,
              max: 100,
            },
            expressionProperties: {
              "templateOptions.disabled": (): boolean => {
                return !this.enableEdit;
              },
            },
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: `subscription.prices[${index++}].discount`,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.priceList.discount"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.priceList.discount"
              ),
              required: false,
              type: "number",
              step: 1,
              min: 0,
              max: 100,
            },
            expressionProperties: {
              "templateOptions.disabled": (): boolean => {
                return !this.enableEdit;
              },
            },
          },
          {
            className: "col-0 col-md-6",
          },
          {
            className: "col-12",
            template: "<hr />",
          },
          {
            className: "col-12 section-label sub-section-label",
            template:
              "<div>" +
              this.translateService.instant(
                `prices.types.${this.prices[index].name}`
              ) +
              "</div>",
          },
          {
            key: `subscription.prices[${index}].key`,
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: `subscription.prices[${index}].price`,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.priceList.price"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.priceList.priceService1"
              ),
              required: false,
              type: "number",
              step: 0.1,
              min: 0,
              max: 100,
            },
            expressionProperties: {
              "templateOptions.disabled": (): boolean => {
                return !this.enableEdit;
              },
            },
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: `subscription.prices[${index++}].discount`,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.priceList.discount"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.priceList.discount"
              ),
              required: false,
              type: "number",
              step: 1,
              min: 0,
              max: 100,
            },
            expressionProperties: {
              "templateOptions.disabled": (): boolean => {
                return !this.enableEdit;
              },
            },
          },
          {
            className: "col-0 col-md-6",
          },
          {
            className: "col-12 section-label sub-section-label",
            template:
              "<div>" +
              this.translateService.instant(
                `prices.types.${this.prices[index].name}`
              ) +
              "</div>",
          },
          {
            key: `subscription.prices[${index}].key`,
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: `subscription.prices[${index}].price`,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.priceList.price"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.priceList.priceService1"
              ),
              required: false,
              type: "number",
              step: 0.1,
              min: 0,
              max: 100,
            },
            expressionProperties: {
              "templateOptions.disabled": (): boolean => {
                return !this.enableEdit;
              },
            },
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: `subscription.prices[${index++}].discount`,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.priceList.discount"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.priceList.discount"
              ),
              required: false,
              type: "number",
              step: 1,
              min: 0,
              max: 100,
            },
            expressionProperties: {
              "templateOptions.disabled": (): boolean => {
                return !this.enableEdit;
              },
            },
          },
          {
            className: "col-0 col-md-6",
          },
          {
            className: "col-12 section-label sub-section-label",
            template:
              "<div>" +
              this.translateService.instant(
                `prices.types.${this.prices[index].name}`
              ) +
              "</div>",
          },
          {
            key: `subscription.prices[${index}].key`,
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: `subscription.prices[${index}].price`,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.priceList.price"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.priceList.priceService1"
              ),
              required: false,
              type: "number",
              step: 0.1,
              min: 0,
              max: 100,
            },
            expressionProperties: {
              "templateOptions.disabled": (): boolean => {
                return !this.enableEdit;
              },
            },
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: `subscription.prices[${index++}].discount`,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.priceList.discount"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.priceList.discount"
              ),
              required: false,
              type: "number",
              step: 1,
              min: 0,
              max: 100,
            },
            expressionProperties: {
              "templateOptions.disabled": (): boolean => {
                return !this.enableEdit;
              },
            },
          },
          {
            className: "col-0 col-md-6",
          },
          {
            className: "col-12 section-label sub-section-label",
            template:
              "<div>" +
              this.translateService.instant(
                `prices.types.${this.prices[index].name}`
              ) +
              "</div>",
          },
          {
            key: `subscription.prices[${index}].key`,
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: `subscription.prices[${index}].price`,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.priceList.price"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.priceList.priceService1"
              ),
              required: false,
              type: "number",
              step: 0.1,
              min: 0,
              max: 100,
            },
            expressionProperties: {
              "templateOptions.disabled": (): boolean => {
                return !this.enableEdit;
              },
            },
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: `subscription.prices[${index++}].discount`,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.priceList.discount"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.priceList.discount"
              ),
              required: false,
              type: "number",
              step: 1,
              min: 0,
              max: 100,
            },
            expressionProperties: {
              "templateOptions.disabled": (): boolean => {
                return !this.enableEdit;
              },
            },
          },
          {
            className: "col-0 col-md-6",
          },
          {
            className: "col-12 section-label sub-section-label",
            template:
              "<div>" +
              this.translateService.instant(
                `prices.types.${this.prices[index].name}`
              ) +
              "</div>",
          },
          {
            key: `subscription.prices[${index}].key`,
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: `subscription.prices[${index}].price`,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.priceList.price"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.priceList.priceService1"
              ),
              required: false,
              type: "number",
              step: 0.1,
              min: 0,
              max: 100,
            },
            expressionProperties: {
              "templateOptions.disabled": (): boolean => {
                return !this.enableEdit;
              },
            },
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: `subscription.prices[${index++}].discount`,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.priceList.discount"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.priceList.discount"
              ),
              required: false,
              type: "number",
              step: 1,
              min: 0,
              max: 100,
            },
            expressionProperties: {
              "templateOptions.disabled": (): boolean => {
                return !this.enableEdit;
              },
            },
          },
          {
            className: "col-0 col-md-6",
          },
          {
            className: "col-12 section-label sub-section-label",
            template:
              "<div>" +
              this.translateService.instant(
                `prices.types.${this.prices[index].name}`
              ) +
              "</div>",
          },
          {
            key: `subscription.prices[${index}].key`,
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: `subscription.prices[${index}].price`,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.priceList.price"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.priceList.priceService1"
              ),
              required: false,
              type: "number",
              step: 0.1,
              min: 0,
              max: 100,
            },
            expressionProperties: {
              "templateOptions.disabled": (): boolean => {
                return !this.enableEdit;
              },
            },
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: `subscription.prices[${index++}].discount`,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.priceList.discount"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.priceList.discount"
              ),
              required: false,
              type: "number",
              step: 1,
              min: 0,
              max: 100,
            },
            expressionProperties: {
              "templateOptions.disabled": (): boolean => {
                return !this.enableEdit;
              },
            },
          },
          {
            className: "col-0 col-md-6",
          },
          {
            className: "col-12",
            template: "<hr />",
          },
          {
            className: "col-12 section-label sub-section-label",
            template:
              "<div>" +
              this.translateService.instant(
                `prices.types.${this.prices[index].name}`
              ) +
              "</div>",
          },
          {
            key: `subscription.prices[${index}].key`,
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: `subscription.prices[${index}].price`,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.priceList.price"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.priceList.priceService1"
              ),
              required: false,
              type: "number",
              step: 0.1,
              min: 0,
              max: 100,
            },
            expressionProperties: {
              "templateOptions.disabled": (): boolean => {
                return !this.enableEdit;
              },
            },
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: `subscription.prices[${index++}].discount`,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.priceList.discount"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.priceList.discount"
              ),
              required: false,
              type: "number",
              step: 1,
              min: 0,
              max: 100,
            },
            expressionProperties: {
              "templateOptions.disabled": (): boolean => {
                return !this.enableEdit;
              },
            },
          },
          {
            className: "col-0 col-md-6",
          },
          {
            className: "col-12",
            template: "<hr />",
          },
          {
            className: "col-12 section-label sub-section-label",
            template:
              "<div>" +
              this.translateService.instant(
                "companies.fields.subscription.additionalStorage.sectionTitle"
              ) +
              "</div>",
          },
          {
            className: "col-3",
            type: "input",
            key: "subscription.extraSpaceFee",
            templateOptions: {
              label: this.translateService.instant(
                "subscriptions.fields.extraSpaceFee"
              ),
              placeholder: this.translateService.instant(
                "subscriptions.fields.extraSpaceFee"
              ),
              type: "number",
              min: 0,
              max: 500,
            },
            expressionProperties: {
              "templateOptions.required":
                "model.subscription.key && model.subscription.key !== 'noSubscription'",
              "templateOptions.disabled": (): boolean => {
                return (
                  !this.enableEdit ||
                  (this.modelSC.subscription.key &&
                    this.modelSC.subscription.key === "noSubscription")
                );
              },
            },
          },
        ],
      },
      // Notes
      {
        fieldGroupClassName: "row",
        fieldGroup: [
          {
            className: "col-12",
            template:
              "<hr /><div><strong>" +
              this.translateService.instant(
                "companies.fields.subscription.notes"
              ) +
              "</strong></div>",
          },
          {
            className: "col-12",
            type: "textarea",
            key: "subscription.notes",
            templateOptions: {
              rows: 5,
            },
            expressionProperties: {
              "templateOptions.disabled": (): boolean => {
                return !this.enableEdit;
              },
            },
          },
        ],
      },
    ];

    this.statusFields = [
      {
        fieldGroupClassName: "row",
        fieldGroup: [
          {
            className: "col-4",
            type: "checkbox",
            key: "propertyStatus.sag.checked",
            templateOptions: {
              label: this.translateService.instant(
                "properties.fields.propertyStatus.sag.checked"
              ),
              placeholder: this.translateService.instant(
                "properties.fields.propertyStatus.sag.checked"
              ),
            },
            expressionProperties: {
              "templateOptions.disabled": (): boolean => {
                return (
                  !this.enableEdit ||
                  this.modelSP.propertyStatus.sag.disabled ||
                  (
                    this.modelSP.propertyStatus &&
                    this.modelSP.propertyStatus.partialMapping &&
                    this.modelSP.propertyStatus.partialMapping.checked
                  )
                );
              },
            },
          },
          {
            className: "col-2",
            type: "datepicker",
            key: "propertyStatus.sag.date",
            templateOptions: {
              label: this.translateService.instant(
                "properties.fields.propertyStatus.sag.date"
              ),
              placeholder: this.translateService.instant(
                "properties.fields.propertyStatus.sag.date"
              ),
              disabled: true
            },
          },
          {
            className: "col-4",
            type: "checkbox",
            key: "propertyStatus.partialMapping.checked",
            templateOptions: {
              label: this.translateService.instant(
                "properties.fields.propertyStatus.partialMapping.checked"
              ),
              placeholder: this.translateService.instant(
                "properties.fields.propertyStatus.partialMapping.checked"
              ),
            },
            expressionProperties: {
              "templateOptions.disabled": (): boolean => {
                return (
                  !this.enableEdit ||
                  this.modelSP.propertyStatus.partialMapping.disabled ||
                  (
                    this.modelSP.propertyStatus &&
                    this.modelSP.propertyStatus.sag &&
                    this.modelSP.propertyStatus.sag.checked
                  )
                );
              },
            },
          },
        ]
      },
      {
        fieldGroupClassName: "row",
        fieldGroup: [
          {
            className: "col-4",
            type: "checkbox",
            key: "propertyStatus.mappingConfirmed.checked",
            templateOptions: {
              label: this.translateService.instant(
                "properties.fields.propertyStatus.mappingConfirmed.checked"
              ),
              placeholder: this.translateService.instant(
                "properties.fields.propertyStatus.mappingConfirmed.checked"
              ),
            },
            expressionProperties: {
              "templateOptions.disabled": (): boolean => {
                return (
                  !this.enableEdit ||
                  this.modelSP.propertyStatus.mappingConfirmed.disabled ||
                  (
                    this.modelSP.propertyStatus &&
                    this.modelSP.propertyStatus.partialMapping &&
                    this.modelSP.propertyStatus.partialMapping.checked
                  )
                );
              },
            },
          },
          {
            className: "col-2",
            type: "datepicker",
            key: "propertyStatus.mappingConfirmed.date",
            templateOptions: {
              label: this.translateService.instant(
                "properties.fields.propertyStatus.mappingConfirmed.date"
              ),
              placeholder: this.translateService.instant(
                "properties.fields.propertyStatus.mappingConfirmed.date"
              ),
            },
            expressionProperties: {
              "templateOptions.disabled": (): boolean => {
                return (
                  !this.enableEdit ||
                  this.modelSP.propertyStatus.mappingConfirmed.disabled ||
                  (
                    this.modelSP.propertyStatus &&
                    this.modelSP.propertyStatus.partialMapping &&
                    this.modelSP.propertyStatus.partialMapping.checked
                  )
                );
              },
            },
          },
          {
            className: "col-2",
            type: "checkbox",
            key: "propertyStatus.mappingConfirmed.otherActivity",
            templateOptions: {
              label: this.translateService.instant(
                "properties.fields.propertyStatus.mappingConfirmed.otherActivity"
              ),
              placeholder: this.translateService.instant(
                "properties.fields.propertyStatus.mappingConfirmed.otherActivity"
              ),
            },
            expressionProperties: {
              "templateOptions.disabled": (): boolean => {
                return (
                  !this.enableEdit ||
                  this.modelSP.propertyStatus.mappingConfirmed.disabled ||
                  (
                    this.modelSP.propertyStatus &&
                    this.modelSP.propertyStatus.partialMapping &&
                    this.modelSP.propertyStatus.partialMapping.checked
                  )
                );
              },
            },
          },
          {
            className: "col-4",
            type: "input",
            key: "propertyStatus.mappingConfirmed.otherActivityNote",
            templateOptions: {
              label: this.translateService.instant(
                "properties.fields.propertyStatus.mappingConfirmed.otherActivityNote"
              ),
              placeholder: this.translateService.instant(
                "properties.fields.propertyStatus.mappingConfirmed.otherActivityNote"
              ),
            },
            expressionProperties: {
              "templateOptions.disabled": (): boolean => {
                return (
                  !this.enableEdit ||
                  this.modelSP.propertyStatus.mappingConfirmed.disabled ||
                  (
                    this.modelSP.propertyStatus &&
                    this.modelSP.propertyStatus.partialMapping &&
                    this.modelSP.propertyStatus.partialMapping.checked
                  )
                );
              },
            },
          },
        ]
      },
      {
        fieldGroupClassName: "row",
        fieldGroup: [
          {
            className: "col-4",
            type: "checkbox",
            key: "propertyStatus.mappingCompleted.checked",
            templateOptions: {
              label: this.translateService.instant(
                "properties.fields.propertyStatus.mappingCompleted.checked"
              ),
              placeholder: this.translateService.instant(
                "properties.fields.propertyStatus.mappingCompleted.checked"
              ),
            },
            expressionProperties: {
              "templateOptions.disabled": (): boolean => {
                return (
                  !this.enableEdit ||
                  this.modelSP.propertyStatus.mappingCompleted.disabled ||
                  (
                    this.modelSP.propertyStatus &&
                    this.modelSP.propertyStatus.partialMapping &&
                    this.modelSP.propertyStatus.partialMapping.checked
                  )
                );
              },
            },
          },
          {
            className: "col-2",
            type: "datepicker",
            key: "propertyStatus.mappingCompleted.date",
            templateOptions: {
              label: this.translateService.instant(
                "properties.fields.propertyStatus.mappingCompleted.date"
              ),
              placeholder: this.translateService.instant(
                "properties.fields.propertyStatus.mappingCompleted.date"
              ),
              disabled: true
            },
          },
          {
            className: "col-6",
            type: "input",
            key: "propertyStatus.mappingCompleted.username",
            templateOptions: {
              label: this.translateService.instant(
                "properties.fields.propertyStatus.mappingCompleted.user"
              ),
              placeholder: this.translateService.instant(
                "properties.fields.propertyStatus.mappingCompleted.user"
              ),
              disabled: true
            },
          },
        ]
      },
      {
        fieldGroupClassName: "row",
        fieldGroup: [
          {
            className: "col-4",
            type: "checkbox",
            key: "propertyStatus.insertionConfirmed.checked",
            templateOptions: {
              label: this.translateService.instant(
                "properties.fields.propertyStatus.insertionConfirmed.checked"
              ),
              placeholder: this.translateService.instant(
                "properties.fields.propertyStatus.insertionConfirmed.checked"
              ),
            },
            expressionProperties: {
              "templateOptions.disabled": (): boolean => {
                return (
                  !this.enableEdit ||
                  this.modelSP.propertyStatus.insertionConfirmed.disabled ||
                  (
                    this.modelSP.propertyStatus &&
                    this.modelSP.propertyStatus.partialMapping &&
                    this.modelSP.propertyStatus.partialMapping.checked
                  )
                );
              },
            },
          },
          {
            className: "col-2",
            type: "datepicker",
            key: "propertyStatus.insertionConfirmed.date",
            templateOptions: {
              label: this.translateService.instant(
                "properties.fields.propertyStatus.insertionConfirmed.date"
              ),
              placeholder: this.translateService.instant(
                "properties.fields.propertyStatus.insertionConfirmed.date"
              ),
            },
            expressionProperties: {
              "templateOptions.disabled": (): boolean => {
                return (
                  !this.enableEdit ||
                  this.modelSP.propertyStatus.insertionConfirmed.disabled ||
                  (
                    this.modelSP.propertyStatus &&
                    this.modelSP.propertyStatus.partialMapping &&
                    this.modelSP.propertyStatus.partialMapping.checked
                  )
                );
              },
            },
          },
        ]
      },
      {
        fieldGroupClassName: "row",
        fieldGroup: [
          {
            className: "col-4",
            type: "checkbox",
            key: "propertyStatus.insertionCompleted.checked",
            templateOptions: {
              label: this.translateService.instant(
                "properties.fields.propertyStatus.insertionCompleted.checked"
              ),
              placeholder: this.translateService.instant(
                "properties.fields.propertyStatus.insertionCompleted.checked"
              ),
            },
            expressionProperties: {
              "templateOptions.disabled": (): boolean => {
                return (
                  !this.enableEdit ||
                  this.modelSP.propertyStatus.insertionCompleted.disabled ||
                  (
                    this.modelSP.propertyStatus &&
                    this.modelSP.propertyStatus.partialMapping &&
                    this.modelSP.propertyStatus.partialMapping.checked
                  )
                );
              },
            },
          },
          {
            className: "col-2",
            type: "datepicker",
            key: "propertyStatus.insertionCompleted.date",
            templateOptions: {
              label: this.translateService.instant(
                "properties.fields.propertyStatus.insertionCompleted.date"
              ),
              placeholder: this.translateService.instant(
                "properties.fields.propertyStatus.insertionCompleted.date"
              ),
            },
            expressionProperties: {
              "templateOptions.disabled": (): boolean => {
                return (
                  !this.enableEdit ||
                  this.modelSP.propertyStatus.insertionCompleted.disabled ||
                  (
                    this.modelSP.propertyStatus &&
                    this.modelSP.propertyStatus.partialMapping &&
                    this.modelSP.propertyStatus.partialMapping.checked
                  )
                );
              },
            },
          },
        ]
      },
      {
        fieldGroupClassName: "row",
        fieldGroup: [
          {
            className: "col-4",
            type: "checkbox",
            key: "propertyStatus.startDate.checked",
            templateOptions: {
              label: this.translateService.instant(
                "properties.fields.propertyStatus.startDate.checked"
              ),
              placeholder: this.translateService.instant(
                "properties.fields.propertyStatus.startDate.checked"
              ),
            },
            expressionProperties: {
              "templateOptions.disabled": (): boolean => {
                return (
                  !this.enableEdit ||
                  this.modelSP.propertyStatus.startDate.disabled ||
                  (
                    this.modelSP.propertyStatus &&
                    this.modelSP.propertyStatus.ceased &&
                    this.modelSP.propertyStatus.ceased.checked
                  )
                );
              },
            },
          },
          {
            className: "col-2",
            type: "datepicker",
            key: "propertyStatus.startDate.date",
            templateOptions: {
              label: this.translateService.instant(
                "properties.fields.propertyStatus.startDate.date"
              ),
              placeholder: this.translateService.instant(
                "properties.fields.propertyStatus.startDate.date"
              ),
            },
            expressionProperties: {
              "templateOptions.disabled": (): boolean => {
                return (
                  !this.enableEdit ||
                  this.modelSP.propertyStatus.startDate.disabled ||
                  (
                    this.modelSP.propertyStatus &&
                    this.modelSP.propertyStatus.ceased &&
                    this.modelSP.propertyStatus.ceased.checked
                  )
                );
              },
            },
          },
        ]
      },
      {
        fieldGroupClassName: "row",
        fieldGroup: [
          {
            className: "col-4",
            type: "checkbox",
            key: "propertyStatus.ceased.checked",
            templateOptions: {
              label: this.translateService.instant(
                "properties.fields.propertyStatus.ceased.checked"
              ),
              placeholder: this.translateService.instant(
                "properties.fields.propertyStatus.ceased.checked"
              ),
            },
            expressionProperties: {
              "templateOptions.disabled": (): boolean => {
                return (
                  !this.enableEdit
                );
              },
            },
          },
          {
            className: "col-2",
            type: "datepicker",
            key: "propertyStatus.ceased.date",
            templateOptions: {
              label: this.translateService.instant(
                "properties.fields.propertyStatus.ceased.date"
              ),
              placeholder: this.translateService.instant(
                "properties.fields.propertyStatus.ceased.date"
              ),
            },
            expressionProperties: {
              "templateOptions.disabled": (): boolean => {
                return (
                  !this.enableEdit
                );
              },
            },
          },
        ]
      },
      {
        fieldGroupClassName: "row",
        fieldGroup: [
          {
            className: "col-4",
            type: "checkbox",
            key: "propertyStatus.remapping.checked",
            templateOptions: {
              label: this.translateService.instant(
                "properties.fields.propertyStatus.remapping.checked"
              ),
              placeholder: this.translateService.instant(
                "properties.fields.propertyStatus.remapping.checked"
              ),
            },
            expressionProperties: {
              "templateOptions.disabled": (): boolean => {
                return (
                  !this.enableEdit
                );
              },
            },
          },
          {
            className: "col-2",
            type: "datepicker",
            key: "propertyStatus.remapping.date",
            templateOptions: {
              label: this.translateService.instant(
                "properties.fields.propertyStatus.remapping.date"
              ),
              placeholder: this.translateService.instant(
                "properties.fields.propertyStatus.remapping.date"
              ),
            },
            expressionProperties: {
              "templateOptions.disabled": (): boolean => {
                return (
                  !this.enableEdit ||
                  !this.modelSP.propertyStatus.remapping.checked
                );
              },
            },
          },
          {
            className: "col-6",
            type: "input",
            key: "propertyStatus.remapping.user",
            templateOptions: {
              label: this.translateService.instant(
                "properties.fields.propertyStatus.remapping.user"
              ),
              placeholder: this.translateService.instant(
                "properties.fields.propertyStatus.remapping.user"
              ),
            },
            expressionProperties: {
              "templateOptions.disabled": (): boolean => {
                return (
                  !this.enableEdit ||
                  !this.modelSP.propertyStatus.remapping.checked
                );
              },
            },
          },
        ]
      }
    ]
  }

  /**
   * Autofill subscription values based on subscription type changes
   *
   * @since 1.0.0
   */
  updateSubscriptionCodes(): void {
    const subscriptionControl = this.formSC.controls.subscription;

    // Control fields
    const documentUploadControl = this.formSC.controls.subscription.get(
      "documentUpload"
    );
    const keyControl = this.formSC.controls.subscription.get("key");
    const suppliersControl = this.formSC.controls.subscription.get(
      "suppliers"
    );

    const startDateSuppliersControl =
      this.formSC.controls.subscription.get("startDateSuppliers");
    const nfcAccessControl = this.formSC.controls.subscription.get("nfcAccess");
    const startDateNfcAccessControl =
      this.formSC.controls.subscription.get("startDateNfcAccess");

    // Value fields
    const feeField = subscriptionControl.get("fee");
    const availableSpaceField = subscriptionControl.get("availableSpace");
    const structureField = subscriptionControl.get("structure");
    const structurePriceField = subscriptionControl.get("structurePrice");
    const firstPriceListField = subscriptionControl.get("firstPriceList");
    const scanInsNextField = subscriptionControl.get("scanInsNext");
    const flatRateField = subscriptionControl.get("flatRate");
    const scanNextField = subscriptionControl.get("scanNext");
    const flatRateScanField = subscriptionControl.get("flatRateScan");
    const startDateSuppliersField = subscriptionControl.get("startDateSuppliers");
    const endDateSuppliersField = subscriptionControl.get("endDateSuppliers");
    const suppliersFeeField = subscriptionControl.get('suppliersFee');
    const nfcAccessField = subscriptionControl.get("nfcAccess");
    const startDateNfcAccessField =
      subscriptionControl.get("startDateNfcAccess");
    const endDateNfcAccessField = subscriptionControl.get("endDateNfcAccess");
    const extraSpaceFeeField = subscriptionControl.get("extraSpaceFee");

    // Option fields
    let codeField: FormlyFieldConfig;
    if (this.contractFields && Array.isArray(this.contractFields)) {
      this.contractFields.forEach((el: any) => {
        if (el.fieldGroup && Array.isArray(el.fieldGroup)) {
          el.fieldGroup.forEach((subEl: any) => {
            switch (subEl.key) {
              case "subscription.code":
                codeField = subEl;
                break;
            }
          });
        }
      });
    }

    if (keyControl) {
      keyControl.valueChanges
        .pipe(distinctUntilChanged(), startWith(keyControl.value))
        .subscribe(
          (res) => {
            if (!res) {
              return;
            }

            let filteredCodes = [];
            filteredCodes = [];
            filteredCodes = this.firstScanCodes.filter((code: any) => {
              return code.keys.includes(res);
            });
            filteredCodes = [];
            filteredCodes = [];
            filteredCodes = [];
            filteredCodes = this.scanNextCodesForfait.filter((code: any) => {
              return code.keys.includes(res);
            });

            let index = -1;
            switch (res) {
              case "noSubscription":
              case "PRIVACY":
                feeField.setValue(0);
                availableSpaceField.setValue(0);
                extraSpaceFeeField.setValue(0);
                structurePriceField.setValue(0);
                structureField.setValue(false);
                firstPriceListField.setValue(false);
                scanInsNextField.setValue(false);
                flatRateField.setValue(false);
                scanNextField.setValue(false);
                flatRateScanField.setValue(false);
                nfcAccessField.setValue(false);
                break;
              case "FULL":
                index = this.archediSubscriptions.findIndex((el: any) => {
                  return el.key === "FULL";
                });
                structureField.setValue(true);
                firstPriceListField.setValue(true);
                scanInsNextField.setValue(false);
                flatRateField.setValue(true);
                scanNextField.setValue(true);
                flatRateScanField.setValue(false);
                break;
              case "FULLBASIC":
                index = this.archediSubscriptions.findIndex((el: any) => {
                  return el.key === "FULLBASIC";
                });
                structureField.setValue(true);
                firstPriceListField.setValue(true);
                scanInsNextField.setValue(true);
                flatRateField.setValue(false);
                scanNextField.setValue(true);
                flatRateScanField.setValue(false);
                break;
              case "FREE":
                index = this.archediSubscriptions.findIndex((el: any) => {
                  return el.key === "FREE";
                });
                structureField.setValue(false);
                firstPriceListField.setValue(true);
                scanInsNextField.setValue(false);
                flatRateField.setValue(true);
                scanNextField.setValue(true);
                flatRateScanField.setValue(false);
                break;
              case "FREEBASIC":
                index = this.archediSubscriptions.findIndex((el: any) => {
                  return el.key === "FREEBASIC";
                });
                structureField.setValue(false);
                firstPriceListField.setValue(true);
                scanInsNextField.setValue(true);
                flatRateField.setValue(false);
                scanNextField.setValue(true);
                flatRateScanField.setValue(false);
                break;
            }
            if (index > -1) {
              feeField.setValue(this.archediSubscriptions[index].fee);
              availableSpaceField.setValue(
                this.archediSubscriptions[index].availableSpace
              );
              extraSpaceFeeField.setValue(
                this.archediSubscriptions[index].extraSpaceFee
              );
              structurePriceField.setValue(
                this.archediSubscriptions[index].structurePrice
              );
            }
          },
          (err) => {
            this.helperService.handleError(err);
          }
        );
    }

    if (suppliersControl) {
      suppliersControl.valueChanges
        .pipe(distinctUntilChanged(), startWith(suppliersControl.value))
        .subscribe(
          (res) => {
            if (!res) {
              startDateSuppliersField.setValue(null);
              endDateSuppliersField.setValue(null);
              suppliersFeeField.setValue(null);
            } else {
              startDateSuppliersField.setValue(this.masterStarDateSupplier);
              endDateSuppliersField.setValue(this.masterEndDateSupplier);
              suppliersFeeField.setValue(this.masterSuppliersFee)
            }
          },
          (err) => {
            this.helperService.handleError(err);
          }
        );
    }

    if (documentUploadControl) {
      documentUploadControl.valueChanges
        .pipe(distinctUntilChanged(), startWith(documentUploadControl.value))
        .subscribe(
          (res) => {
            if (res) {
              nfcAccessControl.setValue(true);
            }
          },
          (err) => {
            this.helperService.handleError(err);
          }
        );
    }

    if (startDateSuppliersControl) {
      startDateSuppliersControl.valueChanges
        .pipe(distinctUntilChanged(), startWith(startDateSuppliersControl.value))
        .subscribe(
          (res) => {
            if (!res) {
              return;
            }
            if (res && startDateSuppliersField.dirty) {
              // Set endDate automatically to startDate + 1 year
              const start = new Date(res);
              const end = start;
              end.setDate(start.getDate() - 1);
              end.setFullYear(end.getFullYear() + 1);
              endDateSuppliersField.setValue(end);
            }
          },
          (err) => {
            this.helperService.handleError(err);
          }
        );
    }
    
    if (startDateNfcAccessControl) {
      startDateNfcAccessControl.valueChanges
        .pipe(
          distinctUntilChanged(),
          startWith(startDateNfcAccessControl.value)
        )
        .subscribe(
          (res) => {
            if (!res) {
              return;
            }
            if (res && startDateNfcAccessField.dirty) {
              // Set endDate automatically to startDate + 1 year
              const start = new Date(res);
              const end = start;
              end.setDate(start.getDate() - 1);
              end.setFullYear(end.getFullYear() + 1);
              endDateNfcAccessField.setValue(end);
            }
          },
          (err) => {
            this.helperService.handleError(err);
          }
        );
    }
  }

  /**
   * Init shared user form
   */
  initShareUserFom(): void {
    // Set model
    const propertyId = this.property.get("_id");

    let companyId = this.property.get("admin.company._id");
    if (typeof companyId === "undefined") {
      companyId = this.property.get("admin.company");
    }

    const userRoles = this.sharedUser.get("userRoles");
    let userRoleIndex;
    let propertiesPermissionUser = {};
    if (userRoles && Array.isArray(userRoles)) {
      userRoleIndex = userRoles.findIndex((elem: any) => {
        return elem && elem.company && elem.company._id
          ? elem.company._id.toString() === companyId.toString()
          : false;
      });
    }
    let propertyPerm;
    if (userRoleIndex > -1) {
      propertiesPermissionUser = userRoles[userRoleIndex].propertiesPermission;
    }

    this.modelSharedUser = this.sharedUser;
    if (propertiesPermissionUser) {
      this.modelSharedUser.propertiesPermission = propertiesPermissionUser;
    }

    this.sharedUserFields = [
      {
        fieldGroupClassName: "row",
        fieldGroup: [
          {
            fieldGroupClassName: "col",
            className: "col-12 col-md-6 col-lg-4",
            fieldGroup: [
              {
                key: "companyId",
                defaultValue: companyId,
              },
              {
                key: "propertyId",
                defaultValue: propertyId,
              },
              {
                type: "checkbox",
                key: "propertiesPermission.unlimited",
                defaultValue: false,
                templateOptions: {
                  label: this.translateService.instant(
                    "users.fields.propertiesPermission.unlimited.label"
                  ),
                },
                expressionProperties: {
                  "templateOptions.onClick": (
                    model: any,
                    formState: any,
                    field?: FormlyFieldConfig
                  ): void => {
                    const key = field.key as string;
                    const subKey = key.replace(
                      "propertiesPermission.",
                      ""
                    );
                    if (
                      this.previousPermissions[subKey] !=
                      model.propertiesPermission[subKey]
                    ) {
                      this.previousPermissions[subKey] =
                        model.propertiesPermission[subKey];
                      this.managePermissionCheckbox(model, field, subKey);
                    }
                  },
                },
              },
              {
                key: "propertiesPermission.expiresUnlimited",
                defaultValue: "unlimited",
              },
              {
                template:
                  "<div><strong>" +
                  this.translateService.instant(
                    "users.fields.propertiesPermission.areaMappatura"
                  ) +
                  "</strong></div>",
              },
              {
                type: "checkbox",
                key: "propertiesPermission.mappatura",
                defaultValue: false,
                templateOptions: {
                  label: this.translateService.instant(
                    "users.fields.propertiesPermission.mappatura"
                  ),
                },
                expressionProperties: {
                  "templateOptions.onClick": (
                    model: any,
                    formState: any,
                    field?: FormlyFieldConfig
                  ): void => {
                    const key = field.key as string;
                    const subKey = key.replace(
                      "propertiesPermission.",
                      ""
                    );
                    if (
                      this.previousPermissions[subKey] !=
                      model.propertiesPermission[subKey]
                    ) {
                      this.previousPermissions[subKey] =
                        model.propertiesPermission[subKey];
                      this.managePermissionCheckbox(model, field, subKey);
                    }
                  },
                },
              },
              {
                template:
                  "<br /><div><strong>" +
                  this.translateService.instant(
                    "users.fields.propertiesPermission.areaGest"
                  ) +
                  "</strong></div>",
              },
              {
                type: "checkbox",
                key: "propertiesPermission.gestionaleAmministrativo",
                defaultValue: false,
                templateOptions: {
                  label: this.translateService.instant(
                    "users.fields.propertiesPermission.gestionaleAmministrativo"
                  ),
                },
                expressionProperties: {
                  "templateOptions.onClick": (
                    model: any,
                    formState: any,
                    field?: FormlyFieldConfig
                  ): void => {
                    const key = field.key as string;
                    const subKey = key.replace(
                      "propertiesPermission.",
                      ""
                    );
                    if (
                      this.previousPermissions[subKey] !=
                      model.propertiesPermission[subKey]
                    ) {
                      this.previousPermissions[subKey] =
                        model.propertiesPermission[subKey];
                      this.managePermissionCheckbox(model, field, subKey);
                    }
                  },
                },
              },
              {
                type: "checkbox",
                key: "propertiesPermission.gestionaleContabile",
                templateOptions: {
                  label: this.translateService.instant(
                    "users.fields.propertiesPermission.gestionaleContabile"
                  ),
                },
                expressionProperties: {
                  "templateOptions.onClick": (
                    model: any,
                    formState: any,
                    field?: FormlyFieldConfig
                  ): void => {
                    const key = field.key as string;
                    const subKey = key.replace(
                      "propertiesPermission.",
                      ""
                    );
                    if (
                      this.previousPermissions[subKey] !=
                      model.propertiesPermission[subKey]
                    ) {
                      this.previousPermissions[subKey] =
                        model.propertiesPermission[subKey];
                      this.managePermissionCheckbox(model, field, subKey);
                    }
                  },
                },
              },
              {
                type: "checkbox",
                key: "propertiesPermission.urbanisticaEdEdilizia",
                defaultValue: false,
                templateOptions: {
                  label: this.translateService.instant(
                    "users.fields.propertiesPermission.urbanisticaEdEdilizia"
                  ),
                },
                expressionProperties: {
                  "templateOptions.onClick": (
                    model: any,
                    formState: any,
                    field?: FormlyFieldConfig
                  ): void => {
                    const key = field.key as string;
                    const subKey = key.replace(
                      "propertiesPermission.",
                      ""
                    );
                    if (
                      this.previousPermissions[subKey] !=
                      model.propertiesPermission[subKey]
                    ) {
                      this.previousPermissions[subKey] =
                        model.propertiesPermission[subKey];
                      this.managePermissionCheckbox(model, field, subKey);
                    }
                  },
                },
              },
              {
                type: "checkbox",
                key: "propertiesPermission.catasto",
                defaultValue: false,
                templateOptions: {
                  label: this.translateService.instant(
                    "users.fields.propertiesPermission.catasto"
                  ),
                },
                expressionProperties: {
                  "templateOptions.onClick": (
                    model: any,
                    formState: any,
                    field?: FormlyFieldConfig
                  ): void => {
                    const key = field.key as string;
                    const subKey = key.replace(
                      "propertiesPermission.",
                      ""
                    );
                    if (
                      this.previousPermissions[subKey] !=
                      model.propertiesPermission[subKey]
                    ) {
                      this.previousPermissions[subKey] =
                        model.propertiesPermission[subKey];
                      this.managePermissionCheckbox(model, field, subKey);
                    }
                  },
                },
              },
              {
                type: "checkbox",
                key: "propertiesPermission.contratti",
                defaultValue: false,
                templateOptions: {
                  label: this.translateService.instant(
                    "users.fields.propertiesPermission.contratti"
                  ),
                },
                expressionProperties: {
                  "templateOptions.onClick": (
                    model: any,
                    formState: any,
                    field?: FormlyFieldConfig
                  ): void => {
                    const key = field.key as string;
                    const subKey = key.replace(
                      "propertiesPermission.",
                      ""
                    );
                    if (
                      this.previousPermissions[subKey] !=
                      model.propertiesPermission[subKey]
                    ) {
                      this.previousPermissions[subKey] =
                        model.propertiesPermission[subKey];
                      this.managePermissionCheckbox(model, field, subKey);
                    }
                  },
                },
              },
              {
                template:
                  "<br /><div><strong>" +
                  this.translateService.instant(
                    "users.fields.propertiesPermission.areaUnitaImmob"
                  ) +
                  "<strong></div>",
              },
              {
                type: "checkbox",
                key: "propertiesPermission.unitaImmobiliari",
                defaultValue: false,
                templateOptions: {
                  label: this.translateService.instant(
                    "users.fields.propertiesPermission.unitaImmobiliari"
                  ),
                },
                expressionProperties: {
                  "templateOptions.onClick": (
                    model: any,
                    formState: any,
                    field?: FormlyFieldConfig
                  ): void => {
                    const key = field.key as string;
                    const subKey = key.replace(
                      "propertiesPermission.",
                      ""
                    );
                    if (
                      this.previousPermissions[subKey] !=
                      model.propertiesPermission[subKey]
                    ) {
                      this.previousPermissions[subKey] =
                        model.propertiesPermission[subKey];
                      this.managePermissionCheckbox(model, field, subKey);
                    }
                  },
                },
              },
            ],
          },
          {
            className: "col-12 col-md-6 col-lg-4",
            fieldGroupClassName: "col",
            fieldGroup: [
              {
                template: "<br /><div><strong></strong></div>",
              },
              {
                template:
                  "<br /><div><strong>" +
                  this.translateService.instant(
                    "users.fields.propertiesPermission.areaTecnica"
                  ) +
                  "</strong></div>",
              },
              {
                type: "checkbox",
                key: "propertiesPermission.lineaVita",
                defaultValue: false,
                templateOptions: {
                  label: this.translateService.instant(
                    "users.fields.propertiesPermission.lineaVita"
                  ),
                },
                expressionProperties: {
                  "templateOptions.onClick": (
                    model: any,
                    formState: any,
                    field?: FormlyFieldConfig
                  ): void => {
                    const key = field.key as string;
                    const subKey = key.replace(
                      "propertiesPermission.",
                      ""
                    );
                    if (
                      this.previousPermissions[subKey] !=
                      model.propertiesPermission[subKey]
                    ) {
                      this.previousPermissions[subKey] =
                        model.propertiesPermission[subKey];
                      this.managePermissionCheckbox(model, field, subKey);
                    }
                  },
                },
              },
              {
                type: "checkbox",
                key: "propertiesPermission.amiantoDoc",
                defaultValue: false,
                templateOptions: {
                  label: this.translateService.instant(
                    "users.fields.propertiesPermission.amiantoDoc"
                  ),
                },
                expressionProperties: {
                  "templateOptions.onClick": (
                    model: any,
                    formState: any,
                    field?: FormlyFieldConfig
                  ): void => {
                    const key = field.key as string;
                    const subKey = key.replace(
                      "propertiesPermission.",
                      ""
                    );
                    if (
                      this.previousPermissions[subKey] !=
                      model.propertiesPermission[subKey]
                    ) {
                      this.previousPermissions[subKey] =
                        model.propertiesPermission[subKey];
                      this.managePermissionCheckbox(model, field, subKey);
                    }
                  },
                },
              },
              {
                type: "checkbox",
                key: "propertiesPermission.edile",
                defaultValue: false,
                templateOptions: {
                  label: this.translateService.instant(
                    "users.fields.propertiesPermission.edile"
                  ),
                },
                expressionProperties: {
                  "templateOptions.onClick": (
                    model: any,
                    formState: any,
                    field?: FormlyFieldConfig
                  ): void => {
                    const key = field.key as string;
                    const subKey = key.replace(
                      "propertiesPermission.",
                      ""
                    );
                    if (
                      this.previousPermissions[subKey] !=
                      model.propertiesPermission[subKey]
                    ) {
                      this.previousPermissions[subKey] =
                        model.propertiesPermission[subKey];
                      this.managePermissionCheckbox(model, field, subKey);
                    }
                  },
                },
              },
              {
                type: "checkbox",
                key: "propertiesPermission.impiantoElettrico",
                defaultValue: false,
                templateOptions: {
                  label: this.translateService.instant(
                    "users.fields.propertiesPermission.impiantoElettrico"
                  ),
                },
              },
              {
                type: "checkbox",
                key: "propertiesPermission.riscaldamentoCondizionamento",
                defaultValue: false,
                templateOptions: {
                  label: this.translateService.instant(
                    "users.fields.propertiesPermission.riscaldamentoCondizionamento"
                  ),
                },
                expressionProperties: {
                  "templateOptions.onClick": (
                    model: any,
                    formState: any,
                    field?: FormlyFieldConfig
                  ): void => {
                    const key = field.key as string;
                    const subKey = key.replace(
                      "propertiesPermission.",
                      ""
                    );
                    if (
                      this.previousPermissions[subKey] !=
                      model.propertiesPermission[subKey]
                    ) {
                      this.previousPermissions[subKey] =
                        model.propertiesPermission[subKey];
                      this.managePermissionCheckbox(model, field, subKey);
                    }
                  },
                },
              },
              {
                type: "checkbox",
                key: "propertiesPermission.ascensori",
                defaultValue: false,
                templateOptions: {
                  label: this.translateService.instant(
                    "users.fields.propertiesPermission.ascensori"
                  ),
                },
                expressionProperties: {
                  "templateOptions.onClick": (
                    model: any,
                    formState: any,
                    field?: FormlyFieldConfig
                  ): void => {
                    const key = field.key as string;
                    const subKey = key.replace(
                      "propertiesPermission.",
                      ""
                    );
                    if (
                      this.previousPermissions[subKey] !=
                      model.propertiesPermission[subKey]
                    ) {
                      this.previousPermissions[subKey] =
                        model.propertiesPermission[subKey];
                      this.managePermissionCheckbox(model, field, subKey);
                    }
                  },
                },
              },
              {
                type: "checkbox",
                key: "propertiesPermission.trattamentoAcqua",
                defaultValue: false,
                templateOptions: {
                  label: this.translateService.instant(
                    "users.fields.propertiesPermission.trattamentoAcqua"
                  ),
                },
                expressionProperties: {
                  "templateOptions.onClick": (
                    model: any,
                    formState: any,
                    field?: FormlyFieldConfig
                  ): void => {
                    const key = field.key as string;
                    const subKey = key.replace(
                      "propertiesPermission.",
                      ""
                    );
                    if (
                      this.previousPermissions[subKey] !=
                      model.propertiesPermission[subKey]
                    ) {
                      this.previousPermissions[subKey] =
                        model.propertiesPermission[subKey];
                      this.managePermissionCheckbox(model, field, subKey);
                    }
                  },
                },
              },
              {
                type: "checkbox",
                key: "propertiesPermission.impiantiElettronici",
                defaultValue: false,
                templateOptions: {
                  label: this.translateService.instant(
                    "users.fields.propertiesPermission.impiantiElettronici"
                  ),
                },
                expressionProperties: {
                  "templateOptions.onClick": (
                    model: any,
                    formState: any,
                    field?: FormlyFieldConfig
                  ): void => {
                    const key = field.key as string;
                    const subKey = key.replace(
                      "propertiesPermission.",
                      ""
                    );
                    if (
                      this.previousPermissions[subKey] !=
                      model.propertiesPermission[subKey]
                    ) {
                      this.previousPermissions[subKey] =
                        model.propertiesPermission[subKey];
                      this.managePermissionCheckbox(model, field, subKey);
                    }
                  },
                },
              },
              {
                type: "checkbox",
                key: "propertiesPermission.antincendio",
                defaultValue: false,
                templateOptions: {
                  label: this.translateService.instant(
                    "users.fields.propertiesPermission.antincendio"
                  ),
                },
                expressionProperties: {
                  "templateOptions.onClick": (
                    model: any,
                    formState: any,
                    field?: FormlyFieldConfig
                  ): void => {
                    const key = field.key as string;
                    const subKey = key.replace(
                      "propertiesPermission.",
                      ""
                    );
                    if (
                      this.previousPermissions[subKey] !=
                      model.propertiesPermission[subKey]
                    ) {
                      this.previousPermissions[subKey] =
                        model.propertiesPermission[subKey];
                      this.managePermissionCheckbox(model, field, subKey);
                    }
                  },
                },
              },
              {
                type: "checkbox",
                key: "propertiesPermission.impiantiSpeciali",
                defaultValue: false,
                templateOptions: {
                  label: this.translateService.instant(
                    "users.fields.propertiesPermission.impiantiSpeciali"
                  ),
                },
                expressionProperties: {
                  "templateOptions.onClick": (
                    model: any,
                    formState: any,
                    field?: FormlyFieldConfig
                  ): void => {
                    const key = field.key as string;
                    const subKey = key.replace(
                      "propertiesPermission.",
                      ""
                    );
                    if (
                      this.previousPermissions[subKey] !=
                      model.propertiesPermission[subKey]
                    ) {
                      this.previousPermissions[subKey] =
                        model.propertiesPermission[subKey];
                      this.managePermissionCheckbox(model, field, subKey);
                    }
                  },
                },
              },
              {
                type: "checkbox",
                key: "propertiesPermission.impiantiRicreativiSportivi",
                defaultValue: false,
                templateOptions: {
                  label: this.translateService.instant(
                    "users.fields.propertiesPermission.impiantiRicreativiSportivi"
                  ),
                },
                expressionProperties: {
                  "templateOptions.onClick": (
                    model: any,
                    formState: any,
                    field?: FormlyFieldConfig
                  ): void => {
                    const key = field.key as string;
                    const subKey = key.replace(
                      "propertiesPermission.",
                      ""
                    );
                    if (
                      this.previousPermissions[subKey] !=
                      model.propertiesPermission[subKey]
                    ) {
                      this.previousPermissions[subKey] =
                        model.propertiesPermission[subKey];
                      this.managePermissionCheckbox(model, field, subKey);
                    }
                  },
                },
              },
              {
                type: "checkbox",
                key: "propertiesPermission.preventivi",
                defaultValue: false,
                templateOptions: {
                  label: this.translateService.instant(
                    "users.fields.propertiesPermission.preventivi"
                  ),
                },
                expressionProperties: {
                  "templateOptions.onClick": (
                    model: any,
                    formState: any,
                    field?: FormlyFieldConfig
                  ): void => {
                    const key = field.key as string;
                    const subKey = key.replace(
                      "propertiesPermission.",
                      ""
                    );
                    if (
                      this.previousPermissions[subKey] !=
                      model.propertiesPermission[subKey]
                    ) {
                      this.previousPermissions[subKey] =
                        model.propertiesPermission[subKey];
                      this.managePermissionCheckbox(model, field, subKey);
                    }
                  },
                },
              },
            ],
          },
        ],
      },
    ];
  }

  /**
   * set/unset all permission checkbox on the unlimited permission change event
   */
  managePermissionCheckbox(model: any, field: any, subKey: string): void {
    if (subKey !== "unlimited") {
      this.previousPermissions[subKey] = model.propertiesPermission[subKey];

      let allChkTrue = true;
      for (const property in model.propertiesPermission) {
        if (property !== "unlimited") {
          allChkTrue = allChkTrue && model.propertiesPermission[property];
        }
      }
      if (allChkTrue) {
        this.previousPermissions.unlimited = false;
        model.propertiesPermission.unlimited = false;
        this.formSharedUser
          .get("propertiesPermission.unlimited")
          .setValue(true);
      } else {
        this.previousPermissions.unlimited = false;
        model.propertiesPermission.unlimited = false;
        this.formSharedUser
          .get("propertiesPermission.unlimited")
          .setValue(false);
      }
    } else {
      for (const property in model.propertiesPermission) {
        if (property !== "expiresUnlimited") {
          if (
            typeof this.formSharedUser.value.propertiesPermission[property] !==
            "undefined"
          ) {
            this.formSharedUser
              .get("propertiesPermission." + property)
              .setValue(model.propertiesPermission.unlimited);
            this.previousPermissions[property] =
              model.propertiesPermission.unlimited;
          }
        }
      }
      for (const property in model.properties) {
        if (property !== "expiresUnlimited") {
          if (
            typeof this.formSharedUser.value.properties[property] !==
            "undefined"
          ) {
            this.formSharedUser
              .get("properties." + property)
              .setValue(model.propertiesPermission.unlimited);
            //this.previousPermissions[property] = model.propertiesPermission.unlimited;
          }
        }
      }
    }
  }

  /**
   * Handle form submit
   *
   * @since 1.0.0
   */
  onFormSubmit(formNumber: number): void {
    // Prevent invalid form submit
    if (formNumber === 1) {
      if (this.form.invalid) {
        Object.keys(this.form.controls).forEach((key) => {
          this.form.controls[key].markAsTouched();
        });
        this.toastrService.error(
          this.translateService.instant("common.errors.form_is_invalid")
        );
        console.warn(this.form);
        return;
      }
    }
    if (formNumber === 2) {
      if (this.form.invalid || this.formSC.invalid) {
        Object.keys(this.form.controls).forEach((key) => {
          this.form.controls[key].markAsTouched();
        });
        Object.keys(this.formSC.controls).forEach((key) => {
          this.formSC.controls[key].markAsTouched();
        });
        this.toastrService.error(
          this.translateService.instant("common.errors.form_is_invalid")
        );
        console.warn(this.form);
        console.warn(this.formSC);
        return;
      }
    }
    this.modelSC.subscription.totalStorageSpaceUsed =
      this.previousTotalStorageSpaceUsed;

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant("dialogs.confirm.title"),
        message: this.translateService.instant("properties.dialog.saveMessage"),
        btnOkText: this.translateService.instant("common.cta.confirm"),
        btnCancelText: this.translateService.instant("common.cta.undo"),
      },
      width: "500px",
    });
    dialogRef
      .beforeClosed()
      .pipe(take(1))
      .subscribe((res) => {
        if (!res) {
          return;
        }
        // Remove blank values from model
        Object.keys(this.model).forEach((key: any) => {
          if (!this.model[key] || this.model[key].valueOf() === "") {
            delete this.model[key];
          }
        });
        Object.keys(this.modelSC).forEach((key: any) => {
          if (!this.modelSC[key] || this.modelSC[key].valueOf() === "") {
            delete this.modelSC[key];
          }
        });

        // Activate submit button loading status
        this.formBtnOptions.active = true;

        let subscription: any;
        if (formNumber === 1) {
          subscription = this.model.subscription
            ? this.model.subscription
            : undefined;
          if (!subscription) {
            this.toastrService.error(
              this.translateService.instant("common.errors.form_is_invalid")
            );
            return;
          }
        }
        if (formNumber === 2) {
          subscription = this.modelSC.subscription
            ? this.modelSC.subscription
            : undefined;
          if (!subscription) {
            this.toastrService.error(
              this.translateService.instant("common.errors.form_is_invalid")
            );
            return;
          }
        }

        // START - Create balance details on subscriptions
        let description = "";

        const detail = [];
        if (subscription.documentUpload) {
          detail.push({
            code: subscription.key,
            description,
            quantity: 1,
            unitPrice: subscription.fee,
            totalRow: subscription.fee,
          });
        }
        // structure
        if (
          subscription.structure &&
          subscription.key !== "FREE" &&
          subscription.key !== "FREEBASIC"
        ) {
          description = "";
          detail.push({
            code: "", 
            description,
            quantity: 1,
            unitPrice: subscription.structurePrice,
            totalRow: subscription.structurePrice,
          });
        }

        if (subscription.suppliers) {
          const sub = this.configService.availableOptionsCodes.filter(
            (el: any) => {
              return el.value === "SPL001";
            }
          )[0];
          description = "";
          description =
            sub && sub.description ? (sub.description as string) : "";
          description = description.replace(/\s\s+/g, " ");
          description = description.replace(
            "#year#",
            moment(subscription.startDateSuppliers).year().toString()
          );
          detail.push({
            code: "SPL001",
            description,
            quantity: 1,
            unitPrice: subscription.suppliersFee,
            totalRow: subscription.suppliersFee,
          });
        }

        const newSubscription = { ...subscription, detail };
        this.model = { ...this.model, subscription: newSubscription };
        // END - Create balance details on subscriptions

        this.subscriptions.push(
          this.propertiesService
            .update(this.property.getId(), this.model)
            .pipe(finalize(() => (this.formBtnOptions.active = false)))
            .subscribe(
              (res) => {
                if (res) {
                  this.toastrService.success(res.message);
                  this.enableEdit = false;
                }
              },
              (err) => {
                // Choose one of the following error handling method.
                // 1) First one show a message right under the form fields
                //    (if the form is properly setted)
                // 2) Second one is used to show a mat-error under each field
                //    in the dynamic form (is meant to be used only in dynamicForm)
                // 3) Third one show toastr notifications for each error
                // 4) Fourth one is used to show mat-error under each field
                //    in the ngx-formly form (is mean to be used only with ngx-formly)
                // this.helperService.handleFormError(form, err);
                // this.helperService.handleDynamicFormError(form, err);
                this.helperService.handleError(err);
                this.helperService.handleFormlyError(this.form, err);
              }
            )
        );
      });
  }

  onFormStatusSubmit(): void {
    // Prevent invalid form submit
    if (this.formSP.invalid) {
      this.toastrService.error(
        this.translateService.instant("common.errors.form_is_invalid")
      );
      console.warn(this.formSP);
      return;
    }

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant("dialogs.confirm.title"),
        message: this.translateService.instant("users.dialog.saveMessage"),
        btnOkText: this.translateService.instant("common.cta.confirm"),
        btnCancelText: this.translateService.instant("common.cta.undo"),
      },
      width: "500px",
    });

    dialogRef
      .beforeClosed()
      .pipe(take(1))
      .subscribe((res) => {
        if (!res) {
          return;
        }
        // Remove blank values from model
        Object.keys(this.modelSP).forEach((key: any) => {
          if (!this.modelSP[key] || this.modelSP[key].valueOf() === "") {
            delete this.modelSP[key];
          }
        });

        const propertyStatus: any = this.modelSP.propertyStatus;
        if (
          propertyStatus &&
          propertyStatus.mappingCompleted &&
          propertyStatus.mappingCompleted.user
        ) {
          delete propertyStatus.mappingCompleted.username;
          propertyStatus.mappingCompleted.user = this.modelSP.propertyStatus.mappingCompleted.user._id;
        }

        const sendData = {
          propertyStatus: propertyStatus
        }

        // Activate submit button loading status
        this.formBtnOptions.active = true;

        this.subscriptions.push(
          this.propertiesService
            .updateFields(this.propertyData._id, sendData)
            .pipe(
              finalize(() => (this.formBtnOptions.active = false))
            )
            .subscribe(
              (res) => {
                if (res) {
                  this.toastrService.success(res.message);
                }
              },
              (err) => {
                // Choose one of the following error handling method.
                // 1) First one show a message right under the form fields
                //    (if the form is properly setted)
                // 2) Second one is used to show a mat-error under each field
                //    in the dynamic form (is meant to be used only in dynamicForm)
                // 3) Third one show toastr notifications for each error
                // 4) Fourth one is used to show mat-error under each field
                //    in the ngx-formly form (is mean to be used only with ngx-formly)
                // this.helperService.handleFormError(form, err);
                // this.helperService.handleDynamicFormError(form, err);
                this.helperService.handleError(err);
                this.helperService.handleFormlyError(this.form, err);
              }
            )
        );
      });
  }

  onFormSharedUserSubmit(model: any): void {
    // Prevent invalid form submit
    if (this.formSharedUser.invalid) {
      this.toastrService.error(
        this.translateService.instant("common.errors.form_is_invalid")
      );
      console.warn(this.form);
      return;
    }

    if (
      model.userRoles &&
      Array.isArray(model.userRoles) &&
      model.userRoles.length
    ) {
      if (
        model.userRoles[0].propertiesPermission &&
        model.userRoles[0].propertiesPermission.expiresUnlimited
      ) {
        model.userRoles[0].propertiesPermission.expiresUnlimited = "unlimited";
      }
    }

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant("dialogs.confirm.title"),
        message: this.translateService.instant("users.dialog.saveMessage"),
        btnOkText: this.translateService.instant("common.cta.confirm"),
        btnCancelText: this.translateService.instant("common.cta.undo"),
      },
      width: "500px",
    });

    const property = this.formSharedUser.value.propertyId
      ? this.formSharedUser.value.propertyId
      : this.formSharedUser.controls.propertyId.value;
    if (property) {
      model.propertiesPermission.property = [];
      model.propertiesPermission.property.push(property);
    }

    dialogRef
      .beforeClosed()
      .pipe(take(1))
      .subscribe((res) => {
        if (!res) {
          return;
        }
        // Remove blank values from model
        Object.keys(model).forEach((key: any) => {
          if (!model[key] || model[key].valueOf() === "") {
            delete model[key];
          }
        });

        // Activate submit button loading status
        this.formSharedUserBtnOptions.active = true;

        this.subscriptions.push(
          this.userService
            .updateFields(this.sharedUser.getId(), model)
            .pipe(
              finalize(() => (this.formSharedUserBtnOptions.active = false))
            )
            .subscribe(
              (res) => {
                if (res) {
                  this.toastrService.success(res.message);
                }
              },
              (err) => {
                // Choose one of the following error handling method.
                // 1) First one show a message right under the form fields
                //    (if the form is properly setted)
                // 2) Second one is used to show a mat-error under each field
                //    in the dynamic form (is meant to be used only in dynamicForm)
                // 3) Third one show toastr notifications for each error
                // 4) Fourth one is used to show mat-error under each field
                //    in the ngx-formly form (is mean to be used only with ngx-formly)
                // this.helperService.handleFormError(form, err);
                // this.helperService.handleDynamicFormError(form, err);
                this.helperService.handleError(err);
                this.helperService.handleFormlyError(this.form, err);
              }
            )
        );
      });
  }

  /**
   * Set current user to inactive
   *
   * @since 1.0.0
   */
  onDelete(): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant(
          "properties.detail.deleteTab.confirmDeletion.title"
        ),
        message: this.translateService.instant(
          "properties.detail.deleteTab.confirmDeletion.message"
        ),
        btnOkText: this.translateService.instant("common.cta.confirm"),
        btnCancelText: this.translateService.instant("common.cta.undo"),
      },
      width: "500px",
    });
    // Subscribe to dialog result (only for 1 emit thanks to "take()")
    dialogRef
      .beforeClosed()
      .pipe(take(1))
      .subscribe((res) => {
        if (!res) {
          return;
        }
        this.subscriptions.push(
          this.propertiesService
            .delete(this.property.getId())
            .pipe(finalize(() => (this.deleteBtnOptions.active = false)))
            .subscribe(
              (res2) => {
                this.toastrService.success(res2.message);
                // Once the user is deleted, redirect to list
                this.router.navigate([
                  this.lang,
                  "app",
                  "properties",
                  "suspended",
                ]);
                return;
              },
              (err) => {
                // Choose one of the following error handling
                // method. The first one shows a message right
                // under the form fields (if the form is properly
                // setted), the second one shows toastr
                // notifications for each error
                // this.helperService.handleFormError(this.passwordForm, err);
                this.helperService.handleError(err);
              }
            )
        );
      });
  }

  /**
   * Reactivate current user
   *
   * @since 1.0.0
   */
  onReactivate(): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant(
          "properties.detail.deleteTab.confirmActivation.title"
        ),
        message: this.translateService.instant(
          "properties.detail.deleteTab.confirmActivation.message"
        ),
        btnOkText: this.translateService.instant("common.cta.confirm"),
        btnCancelText: this.translateService.instant("common.cta.undo"),
      },
      width: "500px",
    });

    // Subscribe to dialog result (only for 1 emit thanks to "take()")
    dialogRef
      .beforeClosed()
      .pipe(take(1))
      .subscribe((res) => {
        if (!res) {
          return;
        }
        this.subscriptions.push(
          this.propertiesService
            .activate(this.property.getId())
            .pipe(finalize(() => (this.reactivateBtnOptions.active = false)))
            .subscribe(
              (res2) => {
                this.toastrService.success(res2.message);
                // Once the user is deleted, redirect to list
                this.router.navigate([this.lang, "app", "properties", "list"]);
                return;
              },
              (err) => {
                // Choose one of the following error handling
                // method. The first one shows a message right
                // under the form fields (if the form is properly
                // setted), the second one shows toastr
                // notifications for each error
                // this.helperService.handleFormError(this.passwordForm, err);
                this.helperService.handleError(err);
              }
            )
        );
      });
  }

  /**
   * Toggle activation in all list elements
   *
   * @since 1.0.0
   */
  onToggleSelected(element: MatSlideToggleChange): void {
    if (!element.checked) {
      // Prevent invalid form submit
      if (this.form.invalid) {
        Object.keys(this.form.controls).forEach((key) => {
          this.form.controls[key].markAsTouched();
        });
        this.toastrService.error(
          this.translateService.instant("common.errors.form_is_invalid")
        );
        element.source.toggle();
        return;
      }
    }
    this.enableEdit = element.checked;
  }

  /**
   * Subscribe to lists settings service to allow this list
   * to remember current filters once users navigate to another page.
   * In this way once user will come back to this page he will
   * have all previous settings (such as page number, page size, search, ...)
   *
   * @since 1.0.0
   */
  subscribeToListsSettings(name: string): void {
    this.listsService.enable();
    this.subscriptions.push(
      this.listsService.listsSettings$.subscribe((settings: ListSetting[]) => {
        if (settings.length) {
          settings.forEach((setting: ListSetting) => {
            if (setting.name === name && name === "balances") {
              if (setting.pageIndex) {
                this.balancesPaginator.pageIndex = setting.pageIndex;
              }
              if (setting.pageSize) {
                this.balancesPaginator.pageSize = setting.pageSize;
              }
              if (setting.search && setting.search.general) {
                this.balancesInput.nativeElement.value = setting.search.general;
              }
              if (setting.search && setting.search.number) {
                this.numberFilter.setValue(setting.search.number);
              }
              if (setting.search && setting.search.status) {
                this.statusFilter.setValue(setting.search.status);
              }
              if (setting.search && setting.search.total) {
                this.totalFilter.setValue(setting.search.total);
              }
              if (setting.search && setting.search.discount) {
                this.discountFilter.setValue(setting.search.discount);
              }
              if (setting.search && setting.search.createdAt) {
                this.createdAtFilter.setValue(setting.search.createdAt);
              }
            }
          });
        }
      })
    );
  }

  /**
   * Export properties in a file with given format
   *
   * @since 1.0.0
   */
  onExport(target: string, propertyId: string): void {
    if (target && typeof propertyId !== "undefined") {
      // Handle export for missing/inserted documents
      const date = formatDate(new Date(), "yyyyMMddhhmmss", "en-EN");
      const fileName = `${this.translateService.instant(
        `properties.list.${target}`
      )} ${date}`;
      this.subscriptions.push(
        this.propertiesService.getDocExport(target, propertyId).subscribe(
          (res) => {
            const fileBlob = res;
            const blob = new Blob([fileBlob], {
              type: "application/vnd.ms-excel",
            });
            FileSaver.saveAs(blob, `${fileName}.xlsx`);
          },
          (err) => {
            this.helperService.handleError(err);
          }
        )
      );
    }
  }

  /**
   * Init balances data table
   *
   * @since 1.0.0
   */
  initBalancesDataTable(): void {
    // Load first set of data
    this.loadBalancesPage();

    // Reset pagination after sorting
    this.subscriptions.push(
      this.balancesSort.sortChange.subscribe(
        () => (this.balancesPaginator.pageIndex = 0)
      )
    );

    // Once user change sorting OR change page
    // let's reload the page data
    this.subscriptions.push(
      merge(this.balancesSort.sortChange, this.balancesPaginator.page)
        .pipe(tap(() => this.loadBalancesPage()))
        .subscribe()
    );

    // Handle server-side filtering through general search
    this.subscriptions.push(
      fromEvent(this.balancesInput.nativeElement, "keyup")
        .pipe(
          debounceTime(this.configService.debounceTime), // max 1 query every 350 milliseconts
          distinctUntilChanged(), // Eliminate duplicate values
          tap(() => {
            this.balancesPaginator.pageIndex = 0; // Reset page
            this.loadBalancesPage();
          })
        )
        .subscribe()
    );

    // Handle server-side filtering through column-specific search
    // Why do we use "null as string"? --> @see https://github.com/ReactiveX/rxjs/issues/4772
    combineLatest([
      this.numberFilter.valueChanges.pipe(startWith(null as string)),
      this.statusFilter.valueChanges.pipe(startWith(null as string)),
      this.totalFilter.valueChanges.pipe(startWith(null as string)),
      this.discountFilter.valueChanges.pipe(startWith(null as string)),
      this.createdAtFilter.valueChanges.pipe(startWith(null as string)),
    ])
      .pipe(
        debounceTime(this.configService.debounceTime), // max 1 query every 350 milliseconts
        distinctUntilChanged() // Eliminate duplicate values
      )
      .subscribe((res) => {
        // Load page only if at least one value is not null
        if (!res.every((value) => value === null)) {
          this.loadBalancesPage();
        }
      });
  }

  /**
   * Load requested data page
   *
   * @since 1.0.0
   */
  loadBalancesPage(): void {
    const start =
      this.balancesPaginator.pageIndex * this.balancesPaginator.pageSize;
    const length = this.balancesPaginator.pageSize;
    const search = {
      general: this.balancesInput.nativeElement.value,
      number: this.numberFilter.value,
      status: this.statusFilter.value,
      total: this.totalFilter.value,
      discount: this.discountFilter.value,
      createdAt: this.createdAtFilter.value
        ? new Date(this.createdAtFilter.value).toISOString()
        : undefined,
    };
    this.balancesDataSource.loadPropertyItems(
      this.property.getId(),
      start,
      length,
      this.balancesSort.active,
      this.balancesSort.direction,
      search
    );
  }

  /**
   * Export balances in a file with given format
   *
   * @since 1.0.0
   */
  onExportBalances(): void {
    const date = formatDate(new Date(), "yyyyMMddhhmmss", "en-EN");
    const fileName = `${this.translateService.instant(
      "balances.list.exportFileName"
    )} ${date}`;
    this.subscriptions.push(
      this.balancesService.getExportProperty(this.propertyId).subscribe(
        (res) => {
          const fileBlob = res;
          const blob = new Blob([fileBlob], {
            type: "application/vnd.ms-excel",
          });
          FileSaver.saveAs(blob, `${fileName}.xlsx`);
        },
        (err) => {
          this.helperService.handleError(err);
        }
      )
    );
  }

  /**
   * Reset table's filters
   *
   * @since 1.0.0
   */
  onResetFilters(): void {
    // Reset lists service data
    this.listsService.setListSetting("balances", 0, 10, {});

    // Reset tables' filters
    this.balancesInput.nativeElement.value = "";
    this.numberFilter.setValue(null);
    this.statusFilter.setValue(null);
    this.totalFilter.setValue(null);
    this.discountFilter.setValue(null);
    this.createdAtFilter.setValue(null);

    // Reset tables' sorting versus and column
    this.balancesSort.direction = "desc";
    this.balancesSort.active = "createdAt";
    this.balancesSort._stateChanges.next();

    // Reset balancesPaginator
    this.balancesPaginator.pageIndex = 0;
    this.balancesPaginator.pageSize = 10;

    // Reload table's data
    if (this.loggedUserPermissions.includes("canReadBalancesList")) {
      this.loadBalancesPage();
    }
  }

  /**
   * Reset given filter value and reload the page
   *
   * @since 1.0.0
   */
  onResetFilter(filter: FormControl): void {
    filter.reset();
    if (this.loggedUserPermissions.includes("canReadBalancesList")) {
      this.loadBalancesPage();
    }
  }

  /**
   * Reset general search input and dispath a 'keyup' event
   * so that the table re-render his rows
   *
   * @since 1.0.0
   */
  resetInput(): void {
    this.balancesInput.nativeElement.value = "";
    const event = new KeyboardEvent("keyup");
    this.balancesInput.nativeElement.dispatchEvent(event);
  }

  /**
   * Handle new message dialog open
   *
   * @since 1.0.0
   */
  onAddCommunication(element: any): void {
    if (!element) {
      return;
    }

    // Open dialog
    const newMessageDialogRef = this.newMessageDialog.open(
      AddCommunicationDialogComponent,
      {
        minWidth: "60%",
        maxHeight: "98vh",
        data: {
          recipients: [
            {
              email: element.email,
              name: `${element.firstName} ${element.lastName}`,
            },
          ],
          phonebook: this.property.get("phonebook"),
        },
        disableClose: true,
      }
    );

    // Subscribe to dialog result (only for 1 emit thanks to "take()")
    newMessageDialogRef
      .beforeClosed()
      .pipe(take(1))
      .subscribe((res) => {
        if (!res) {
          return;
        }
        this.toastrService.success(
          this.translateService.instant("communications.add.sendedSuccessfully")
        );
      });
  }

  /**
   * Send shared user data by email
   *
   * @since 1.0.0
   */
  onSendByEmail(): void {
    if (!this.sharedUser) {
      this.toastrService.error(
        this.translateService.instant("common.errors.not_found")
      );
      return;
    }

    const propertyName = this.property.get("name");
    const address = `${this.property.get("address.route")}, ${this.property.get("address.streetNumber")} - ${this.property.get("address.postalCode")} ${this.property.get("address.city")}`;

    const bodyP1 = this.translateService.instant('properties.shared.email.p1',
      { 
        ownerCompany: this.companyName,
        address
      }
    );
    const bodyP2 = this.translateService.instant('properties.shared.email.p2',
      { 
        userName: this.sharedUser.get("email"),
        password: this.model.taxCode
      }
    );
    const bodyP3 = this.translateService.instant('properties.shared.email.p3');

    // Create predefined text with shared user data
    const subject = this.translateService.instant('properties.shared.email.subject', {
      propertyName,
      address
    });
    
    const body = `${bodyP1}

${bodyP2}

${bodyP3}`;

    // Open dialog
    const newMessageDialogRef = this.newMessageDialog.open(
      AddCommunicationDialogComponent,
      {
        minWidth: "60%",
        maxHeight: "98vh",
        data: {
          phonebook: this.property.get("phonebook")
            ? this.property.get("phonebook")
            : [],
          subject,
          body,
          sendSharedUser: true,
        },
        disableClose: true,
      }
    );

    // Subscribe to dialog result (only for 1 emit thanks to "take()")
    newMessageDialogRef
      .beforeClosed()
      .pipe(take(1))
      .subscribe((res) => {
        if (!res) {
          return;
        }
        this.toastrService.success(
          this.translateService.instant("communications.add.sendedSuccessfully")
        );
      });
  }

  // Copy email from admin company
  onAdminCompanySelected(company?: ICompany): void {
    if (!this.model.admin || !company) {
      return;
    }

    if (company) {
      if (company.email && company.email !== "") {
        const notifyMail1Control = this.form.controls.notifyMail1;
        notifyMail1Control.setValue(company.email);
      }

      if (company.subscription) {
        Object.keys(company.subscription).forEach((key) => {
          if (key === 'startDateSuppliers') {
            this.masterStarDateSupplier = company.subscription[key];
          }
          if (key === 'endDateSuppliers') {
            this.masterEndDateSupplier = company.subscription[key];
          }
          if (key === 'suppliersFee') {
            this.masterSuppliersFee = company.subscription[key];
          }
        });
      }
    }
  }


  /**
   * Prevent redirect to archive if structure is open
   *
   * @since 1.0.0
   */
   onGoToDocumentArchive(): void {
    if (!this.mappaturaOpen) {
      return;
    }
    this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant('dialogs.confirm.mappaturaOpen.title'),
        message: this.translateService.instant('dialogs.confirm.mappaturaOpen.message'),
        btnCancelText: this.translateService.instant('common.cta.close'),
        messageOnly: true,
      },
      width: '500px',
      disableClose: false,
    });
  }
    
  /**
   * Word contracts export
   *
   * @since 1.0.0
   */
  onExportContracts(): void {
    const dialogRef = this.dialog.open(ExportContractsDialogComponent, {
      minWidth: "50%",
      maxHeight: "98vh",
      data: {
        propertyId: this.propertyId,
      },
      disableClose: true,
    });
    dialogRef
      .beforeClosed()
      .pipe(take(1))
      .subscribe((res) => {
        if (!res) {
          return;
        }
        return;
      });
  }
  
  /**
   * RAS docs export in PDF format
   *
   * @since 1.0.0
   */
  onExportRAS(): void {
    const dialogRef = this.dialog.open(ExportRasDocsDialogComponent, {
      width: "700px",
      data: {
        propertyId: this.propertyId,
      },
      disableClose: true,
    });
    dialogRef
      .beforeClosed()
      .pipe(take(1))
      .subscribe((res) => {
        if (!res) {
          return;
        }

        return;
      });
  }


  /**
   * STATUS CARD
   */

  onStatusToggle(statusType: any, checked: boolean): void {
    console.log(statusType)
    console.log(checked)
    statusType.checked = checked;
  }

  onDateUpdate(statusType: any, event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    console.log(statusType, value)
  }
  
}
